import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { setupApi, authApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTexts } from '../context/TextContext';
import FieldRenderer from '../components/FieldRenderer';
import { formatDate } from '../utils/formatDate';
import CustomSelect from '../components/CustomSelect';

// ─── Sidebar menu definition ────────────────────────────────────────────────
const MENU = [
  {
    key: 'sistema',
    label: 'Sistema',
    items: [
      { key: 'adminPanel',  label: 'Administración' },
      { key: 'parametros',  label: 'Parámetros' },
      { key: 'notifAdmin',  label: 'Notificaciones' },
      { key: 'sqlConsole',  label: 'Consola SQL',  funcId: 40 },
    ],
  },
  {
    key: 'log',
    label: 'Log',
    items: [
      { key: 'logAccesos', label: 'Accesos' },
      { key: 'logBlog',    label: 'Blog' },
    ],
  },
  {
    key: 'diseno',
    label: 'Diseño',
    items: [
      { key: 'filtros',    label: 'Filtros',               funcId: 3  },
      { key: 'filgrupo',   label: 'Filtros por grupo',      funcId: 3  },
      { key: 'temas',      label: 'Temas',                  funcId: 3  },
      { key: 'filtema',    label: 'Filtros por tema',        funcId: 3  },
      { key: 'tiposDoc',   label: 'Tipos de documentos',   funcId: 13 },
      { key: 'vistas',     label: 'Vistas',                funcId: 13 },
      { key: 'campos',     label: 'Campos',                funcId: 11 },
      { key: 'posib',      label: 'Posibilidades',         funcId: 12 },
      { key: 'disenio',    label: 'Diseño de ficha', funcId: 14 },
      { key: 'empresas',   label: 'Empresas' },
      { key: 'secciones',  label: 'Secciones',             funcId: 24 },
      { key: 'areas',      label: 'Áreas',                 funcId: 28 },
      { key: 'privacidad', label: 'Privacidad',            funcId: 9  },
    ],
  },
  {
    key: 'seguridad',
    label: 'Seguridad',
    items: [
      { key: 'grupos',     label: 'Grupos',                funcId: 6  },
      { key: 'perfiles',   label: 'Perfiles',              funcId: 6  },
      { key: 'funciones',  label: 'Funciones',             funcId: 6  },
      { key: 'perfFun',    label: 'Funciones por perfil',  funcId: 7  },
      { key: 'usrPer',     label: 'Usuarios por perfil',   funcId: 8  },
      { key: 'autoriz',    label: 'Autorizaciones',        funcId: 10 },
      { key: 'autrelac',   label: 'Aut. relacionadas',     funcId: 10 },
    ],
  },
  {
    key: 'tesauro',
    label: 'Tesauro',
    items: [
      { key: 'tesauro',    label: 'Tesauro',               funcId: 2  },
      { key: 'descr',      label: 'Descriptores',          funcId: 2  },
      { key: 'descrTdo',   label: 'Desc. por tipo doc',    funcId: 15 },
    ],
  },
];

// ─── Sidebar ─────────────────────────────────────────────────────────────────
function Sidebar({ active, onSelect }) {
  const { user } = useAuth();
  const funcs = user?.functions || [];
  const [open, setOpen] = useState({ sistema: true, diseno: false, seguridad: false, tesauro: false, log: false });

  return (
    <div className="setup-sidebar">
      {MENU.map(section => {
        const visibleItems = section.items.filter(item => !item.funcId || funcs.includes(item.funcId));
        if (visibleItems.length === 0) return null;
        return (
          <div key={section.key} className="setup-sidebar-section">
            <div
              className="setup-sidebar-header"
              onClick={() => setOpen(o => ({ ...o, [section.key]: !o[section.key] }))}
            >
              <i className={`bi bi-chevron-${open[section.key] ? 'down' : 'right'} me-2`} />
              <strong>{section.label}</strong>
            </div>
            {open[section.key] && (
              <ul className="setup-sidebar-list">
                {visibleItems.map(item => (
                  <li
                    key={item.key}
                    className={`setup-sidebar-item${active === item.key ? ' active' : ''}${item.soon ? ' soon' : ''}`}
                    onClick={() => !item.soon && onSelect(item.key)}
                    title={item.soon ? 'Próximamente' : ''}
                  >
                    {item.label}
                    {item.soon && <span className="setup-soon-badge">pronto</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── GenericCrud ─────────────────────────────────────────────────────────────
function GenericCrud({ config }) {
  const { title, api, idField, empty, fromRow, columns, renderForm, extraApis } = config;
  const { user } = useAuth();
  const canMultiEmp = (user?.functions || []).includes(38);

  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState(empty);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [empresas, setEmpresas] = useState([]);
  const [targetEmpId, setTargetEmpId] = useState('');
  const [extraData, setExtraData] = useState({});

  const load = () => {
    setLoading(true);
    api.list()
      .then(r => setRows(r.data))
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    if (canMultiEmp) {
      setupApi.empresas().then(r => setEmpresas(r.data)).catch(() => {});
    }
    if (extraApis) {
      Promise.all(
        Object.entries(extraApis).map(([key, fn]) =>
          fn().then(r => [key, r.data])
        )
      ).then(entries => setExtraData(Object.fromEntries(entries))).catch(() => {});
    }
  }, []);

  const startNew = () => {
    setForm(empty);
    setTargetEmpId('');
    setEditing('new');
    setError('');
  };

  const startEdit = (row) => {
    setForm(fromRow(row));
    setTargetEmpId('');
    setEditing(row);
    setError('');
  };

  const startCopy = (row) => {
    setForm(fromRow(row));
    setTargetEmpId('');
    setEditing('copy');
    setError('');
  };

  const cancel = () => { setEditing(null); setError(''); };

  const isCopyOrNew = editing === 'new' || editing === 'copy';

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      if (isCopyOrNew) {
        await api.create({ ...form, ...(targetEmpId ? { targetEmpId } : {}) });
      } else {
        await api.update(editing[idField], form);
      }
      setEditing(null);
      load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const del = async (row) => {
    if (!window.confirm(`¿Eliminar registro #${row[idField]}?`)) return;
    try {
      await api.remove(row[idField]);
      load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const formTitle = editing === 'copy' ? `Copiar ${title.toLowerCase()}`
                  : editing === 'new'  ? `Nuevo/a`
                  : `Editar`;

  const colCount = columns.length + 1;

  return (
    <div className="setup-content">
      <div className="setup-content-header">
        <h5 className="mb-0">{title}</h5>
        {editing === null && (
          <button className="button color1" onClick={startNew}>
            <i className="bi bi-plus-lg me-1" />Nuevo
          </button>
        )}
      </div>

      {error && <div className="alert alert-danger py-1 px-2 mb-2">{error}</div>}

      {editing !== null && (
        <div className="setup-form mb-3">
          <div className="setup-form-title mb-2">{formTitle}</div>
          <div className="row g-2">
            {editing === 'copy' && canMultiEmp && (
              <div className="col-12 col-md-4">
                <label className="form-label form-label-sm">Empresa destino</label>
                <CustomSelect value={targetEmpId}
                  onChange={e => setTargetEmpId(e.target.value)}>
                  <option value="">— Empresa actual —</option>
                  {empresas.map(e => (
                    <option key={e.Emp_IdEmp} value={e.Emp_IdEmp}>{e.Emp_Descrip}</option>
                  ))}
                </CustomSelect>
              </div>
            )}
            {renderForm(form, set, extraData, isCopyOrNew)}
          </div>
          <div className="mt-2 d-flex gap-2">
            <button className="button color1" onClick={save} disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
            <button className="button" onClick={cancel} disabled={saving}>Cancelar</button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-muted">Cargando…</p>
      ) : (
        <table className="setup-table">
          <thead>
            <tr>
              {columns.map((col, i) => <th key={i}>{col.label}</th>)}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={colCount} className="text-muted text-center py-3">Sin registros</td></tr>
            )}
            {rows.map(row => (
              <tr key={row[idField]} className={editing?.[idField] === row[idField] ? 'table-active' : ''}>
                {columns.map((col, i) => <td key={i}>{col.render(row)}</td>)}
                <td className="text-end text-nowrap">
                  <button className="btn btn-sm btn-link p-0 me-2" title="Editar"
                    onClick={() => startEdit(row)}>
                    <i className="bi bi-pencil" />
                  </button>
                  <button className="btn btn-sm btn-link p-0 me-2" title="Copiar"
                    onClick={() => startCopy(row)}>
                    <i className="bi bi-copy" />
                  </button>
                  <button className="btn btn-sm btn-link p-0 text-danger" title="Eliminar"
                    onClick={() => del(row)}>
                    <i className="bi bi-trash" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Config: Filtros ─────────────────────────────────────────────────────────
const filtrosConfig = {
  title: 'Filtros',
  api: {
    list:   ()        => setupApi.filtros(),
    create: (data)    => setupApi.createFiltro(data),
    update: (id, data)=> setupApi.updateFiltro(id, data),
    remove: (id)      => setupApi.deleteFiltro(id),
  },
  idField: 'Fil_IdFil',
  empty: {
    FIL_DesFil: '', FIL_Query: '', FIL_Orderby: '', FIL_OrdenFil: '',
    Fil_TipoFil: 0, fil_QueryFields: '', fil_CurEmp: 'N', fil_Parent: '',
    Fil_Vista: '',
  },
  fromRow: (row) => ({
    FIL_DesFil:      row.FIL_DesFil      || '',
    FIL_Query:       row.FIL_Query       || '',
    FIL_Orderby:     row.FIL_Orderby     || '',
    FIL_OrdenFil:    row.FIL_OrdenFil    || '',
    Fil_TipoFil:     row.Fil_TipoFil     ?? 0,
    fil_QueryFields: row.fil_QueryFields || '',
    fil_CurEmp:      (row.fil_CurEmp || 'N').trim(),
    fil_Parent:      row.fil_Parent      ?? '',
    Fil_Vista:       (row.Fil_Vista || '').trim(),
  }),
  columns: [
    { label: '#',          render: r => r.Fil_IdFil },
    { label: 'Descripción',render: r => r.FIL_DesFil },
    { label: 'Orden',      render: r => r.FIL_OrdenFil },
    { label: 'Tipo',       render: r => r.Fil_TipoFil },
    { label: 'Query',      render: r => <span className="text-truncate d-inline-block" style={{ maxWidth: 200 }}>{r.FIL_Query}</span> },
  ],
  renderForm: (form, set) => (
    <>
      <div className="col-12 col-md-6">
        <label className="form-label form-label-sm">Descripción *</label>
        <input className="form-control form-control-sm" value={form.FIL_DesFil}
          onChange={e => set('FIL_DesFil', e.target.value)} />
      </div>
      <div className="col-6 col-md-3">
        <label className="form-label form-label-sm">Orden</label>
        <input className="form-control form-control-sm" value={form.FIL_OrdenFil}
          onChange={e => set('FIL_OrdenFil', e.target.value)} />
      </div>
      <div className="col-6 col-md-3">
        <label className="form-label form-label-sm">Tipo</label>
        <input type="number" className="form-control form-control-sm" value={form.Fil_TipoFil}
          onChange={e => set('Fil_TipoFil', parseInt(e.target.value) || 0)} />
      </div>
      <div className="col-12">
        <label className="form-label form-label-sm">Query (WHERE)</label>
        <input className="form-control form-control-sm font-monospace" value={form.FIL_Query}
          onChange={e => set('FIL_Query', e.target.value)} />
      </div>
      <div className="col-12">
        <label className="form-label form-label-sm">Order by</label>
        <input className="form-control form-control-sm font-monospace" value={form.FIL_Orderby}
          onChange={e => set('FIL_Orderby', e.target.value)} />
      </div>
      <div className="col-12 col-md-6">
        <label className="form-label form-label-sm">Query fields</label>
        <input className="form-control form-control-sm" value={form.fil_QueryFields}
          onChange={e => set('fil_QueryFields', e.target.value)} />
      </div>
      <div className="col-6 col-md-3">
        <label className="form-label form-label-sm">Empresa actual</label>
        <CustomSelect value={form.fil_CurEmp}
          onChange={e => set('fil_CurEmp', e.target.value)}>
          <option value="N">No</option>
          <option value="Y">Sí</option>
        </CustomSelect>
      </div>
      <div className="col-6 col-md-3">
        <label className="form-label form-label-sm">Padre</label>
        <input type="number" className="form-control form-control-sm" value={form.fil_Parent}
          onChange={e => set('fil_Parent', e.target.value)} placeholder="—" />
      </div>
      <div className="col-6 col-md-3">
        <label className="form-label form-label-sm">Vista por defecto</label>
        <CustomSelect value={form.Fil_Vista}
          onChange={e => set('Fil_Vista', e.target.value)}>
          <option value="">— (usa tipo de documento)</option>
          <option value="card">card</option>
          <option value="grid">grid</option>
          <option value="calendar">calendar</option>
        </CustomSelect>
      </div>
    </>
  ),
};

// ─── Config: Tipos de documentos ─────────────────────────────────────────────
const tipodocsConfig = {
  title: 'Tipos de documentos',
  api: {
    list:   ()        => setupApi.tipodocs(),
    create: (data)    => setupApi.createTipodoc(data),
    update: (id, data)=> setupApi.updateTipodoc(id, data),
    remove: (id)      => setupApi.deleteTipodoc(id),
  },
  idField: 'Tdo_IdTDoc',
  empty: {
    Tdo_IdTDoc: null,
    Tdo_NombTDoc: '', Tdo_QueryFields: '', Tdo_PreviewFilas: 0,
    Tdo_Folder: '',
    Tdo_RelFiles: 'N', Tdo_Blog: 'N', Tdo_Descrip: 'N', Tdo_RelCont: 'N',
    Tdo_Icono: '',
  },
  fromRow: (row) => ({
    Tdo_IdTDoc:      row.Tdo_IdTDoc      || null,
    Tdo_NombTDoc:    row.Tdo_NombTDoc    || '',
    Tdo_QueryFields: row.Tdo_QueryFields || '',
    Tdo_PreviewFilas:row.Tdo_PreviewFilas ?? 0,
    Tdo_Folder:      row.Tdo_Folder      || '',
    Tdo_RelFiles:    (row.Tdo_RelFiles   || 'N').trim(),
    Tdo_Blog:        (row.Tdo_Blog       || 'N').trim(),
    Tdo_Descrip:     (row.Tdo_Descrip    || 'N').trim(),
    Tdo_RelCont:     (row.Tdo_RelCont    || 'N').trim(),
    Tdo_Icono:       (row.Tdo_Icono      || '').trim(),
  }),
  columns: [
    { label: '#',        render: r => r.Tdo_IdTDoc },
    { label: 'Nombre',   render: r => r.Tdo_NombTDoc },
    { label: 'Folder',   render: r => r.Tdo_Folder },
    { label: 'Arch. relacionados', render: r => (r.Tdo_RelFiles || '').trim() === 'Y' ? 'Sí' : 'No' },
    { label: 'Blog',     render: r => (r.Tdo_Blog     || '').trim() === 'Y' ? 'Sí' : 'No' },
  ],
  renderForm: (form, set, extraData) => (
    <>
      {/* ── fila 1: campos principales ── */}
      <div className="col-12 col-md-3">
        <label className="form-label form-label-sm">Nombre *</label>
        <input className="form-control form-control-sm" value={form.Tdo_NombTDoc}
          onChange={e => set('Tdo_NombTDoc', e.target.value)} />
      </div>
      <div className="col-12 col-md-3">
        <label className="form-label form-label-sm">Folder</label>
        <input className="form-control form-control-sm" value={form.Tdo_Folder}
          onChange={e => set('Tdo_Folder', e.target.value)} />
      </div>
      <div className="col-12 col-md-3">
        <label className="form-label form-label-sm">Query fields</label>
        <input className="form-control form-control-sm" value={form.Tdo_QueryFields}
          onChange={e => set('Tdo_QueryFields', e.target.value)} />
      </div>
      <div className="col-6 col-md-2">
        <label className="form-label form-label-sm">Icono</label>
        <input className="form-control form-control-sm" value={form.Tdo_Icono}
          onChange={e => set('Tdo_Icono', e.target.value)} />
      </div>
      <div className="col-6 col-md-1">
        <label className="form-label form-label-sm">Filas prev.</label>
        <input type="number" className="form-control form-control-sm" value={form.Tdo_PreviewFilas}
          onChange={e => set('Tdo_PreviewFilas', parseInt(e.target.value) || 0)} />
      </div>
      {/* ── fila 2: flags como checkboxes ── */}
      <div className="w-100" />
      {[
        { field: 'Tdo_RelFiles', label: 'Arch. relacionados' },
        { field: 'Tdo_Blog',     label: 'Blog' },
        { field: 'Tdo_Descrip',  label: 'Descriptores' },
        { field: 'Tdo_RelCont',  label: 'Contenido relacionado' },
      ].map(({ field, label }) => (
        <div key={field} className="col-auto d-flex align-items-center gap-1" style={{ fontSize: '0.82rem' }}>
          <input type="checkbox" className="form-check-input mt-0" id={`chk_${field}`}
            checked={form[field] === 'Y'}
            onChange={e => set(field, e.target.checked ? 'Y' : 'N')} />
          <label className="form-check-label" htmlFor={`chk_${field}`}>{label}</label>
        </div>
      ))}
      {/* ── vistas ── */}
      <VisPanel tdocId={form.Tdo_IdTDoc} templates={(extraData || {}).vistemplates} />
    </>
  ),
  extraApis: {
    vistemplates: () => setupApi.vistemplates(),
  },
};

// ─── Config: Campos ──────────────────────────────────────────────────────────
const camposConfig = {
  title: 'Campos',
  api: {
    list:   ()        => setupApi.campos(),
    create: (data)    => setupApi.createCampo(data),
    update: (id, data)=> setupApi.updateCampo(id, data),
    remove: (id)      => setupApi.deleteCampo(id),
  },
  extraApis: {
    tiposCampo: () => setupApi.tiposCampo(),
  },
  idField: 'Cmp_IdCampo',
  empty: {
    Cmp_NombCampo: '', Cmp_TipoCampo: '', Cmp_Predef: '',
    Cmp_OrderBy: '', Cmp_View: '',
  },
  fromRow: (row) => ({
    Cmp_NombCampo: row.Cmp_NombCampo || '',
    Cmp_TipoCampo: (row.Cmp_TipoCampo || '').trim(),
    Cmp_Predef:    row.Cmp_Predef    || '',
    Cmp_OrderBy:   row.Cmp_OrderBy   || '',
    Cmp_View:      row.Cmp_View      || '',
  }),
  columns: [
    { label: '#',      render: r => r.Cmp_IdCampo },
    { label: 'Nombre', render: r => r.Cmp_NombCampo },
    { label: 'Tipo',   render: r => (r.Cmp_TipoCampo || '').trim() },
    { label: 'Predef', render: r => r.Cmp_Predef },
  ],
  renderForm: (form, set, extra) => (
    <>
      <div className="col-12 col-md-6">
        <label className="form-label form-label-sm">Nombre *</label>
        <input className="form-control form-control-sm" value={form.Cmp_NombCampo}
          onChange={e => set('Cmp_NombCampo', e.target.value)} />
      </div>
      <div className="col-6 col-md-3">
        <label className="form-label form-label-sm">Tipo</label>
        <CustomSelect value={form.Cmp_TipoCampo}
          onChange={e => set('Cmp_TipoCampo', e.target.value)}>
          <option value="">— Seleccionar —</option>
          {(extra.tiposCampo || []).map(t => (
            <option key={t.Tcp_IdTcp} value={t.Tcp_IdTcp}>{t.Tcp_IdTcp} — {t.Tcp_Descrip}</option>
          ))}
        </CustomSelect>
      </div>
      <div className="col-12 col-md-6">
        <label className="form-label form-label-sm">Predef</label>
        <input className="form-control form-control-sm" value={form.Cmp_Predef}
          onChange={e => set('Cmp_Predef', e.target.value)} />
      </div>
      <div className="col-12 col-md-6">
        <label className="form-label form-label-sm">Order by</label>
        <input className="form-control form-control-sm" value={form.Cmp_OrderBy}
          onChange={e => set('Cmp_OrderBy', e.target.value)} />
      </div>
      <div className="col-12 col-md-6">
        <label className="form-label form-label-sm">View</label>
        <input className="form-control form-control-sm" value={form.Cmp_View}
          onChange={e => set('Cmp_View', e.target.value)} />
      </div>
    </>
  ),
};

// ─── Config: Posibilidades ───────────────────────────────────────────────────
const posibConfig = {
  title: 'Posibilidades',
  api: {
    list:   ()        => setupApi.posibilidades(),
    create: (data)    => setupApi.createPosibilidad(data),
    update: (id, data)=> setupApi.updatePosibilidad(id, data),
    remove: (id)      => setupApi.deletePosibilidad(id),
  },
  extraApis: {
    campos: () => setupApi.campos(),
  },
  idField: 'Pos_IdPos',
  empty: {
    Cmp_IdCampo: '', Pos_Valor: '', Pos_Habilitado: 1,
  },
  fromRow: (row) => ({
    Cmp_IdCampo:    row.Cmp_IdCampo    ?? '',
    Pos_Valor:      row.Pos_Valor      || '',
    Pos_Habilitado: row.Pos_Habilitado ?? 1,
  }),
  columns: [
    { label: '#',      render: r => r.Pos_IdPos },
    { label: 'Campo',  render: r => r.Cmp_NombCampo || r.Cmp_IdCampo },
    { label: 'Valor',  render: r => r.Pos_Valor },
    { label: 'Estado', render: r => r.Pos_Habilitado ? 'Habilitado' : 'Deshabilitado' },
  ],
  renderForm: (form, set, extra) => (
    <>
      <div className="col-12 col-md-4">
        <label className="form-label form-label-sm">Campo *</label>
        <CustomSelect value={form.Cmp_IdCampo}
          onChange={e => set('Cmp_IdCampo', e.target.value)}>
          <option value="">— Seleccionar —</option>
          {(extra.campos || []).map(c => (
            <option key={c.Cmp_IdCampo} value={c.Cmp_IdCampo}>{c.Cmp_NombCampo}</option>
          ))}
        </CustomSelect>
      </div>
      <div className="col-12 col-md-5">
        <label className="form-label form-label-sm">Valor *</label>
        <input className="form-control form-control-sm" value={form.Pos_Valor}
          onChange={e => set('Pos_Valor', e.target.value)} />
      </div>
      <div className="col-6 col-md-3">
        <label className="form-label form-label-sm">Estado</label>
        <CustomSelect value={form.Pos_Habilitado}
          onChange={e => set('Pos_Habilitado', parseInt(e.target.value))}>
          <option value={1}>Habilitado</option>
          <option value={0}>Deshabilitado</option>
        </CustomSelect>
      </div>
    </>
  ),
};

// ─── Config: Secciones ───────────────────────────────────────────────────────
const seccionesConfig = {
  title: 'Secciones',
  api: {
    list:   ()        => setupApi.secciones(),
    create: (data)    => setupApi.createSeccion(data),
    update: (id, data)=> setupApi.updateSeccion(id, data),
    remove: (id)      => setupApi.deleteSeccion(id),
  },
  idField: 'Sec_IdSec',
  empty: {
    Sec_DesSec: '', Sec_PageSec: '', Sec_OrdenSec: '',
    Sec_href1: '', Sec_target1: '', Sec_Img1: '',
    Sec_href2: '', Sec_target2: '', Sec_Img2: '',
  },
  fromRow: (row) => ({
    Sec_DesSec:   row.Sec_DesSec   || '',
    Sec_PageSec:  row.Sec_PageSec  || '',
    Sec_OrdenSec: row.Sec_OrdenSec || '',
    Sec_href1:    row.Sec_href1    || '',
    Sec_target1:  row.Sec_target1  || '',
    Sec_Img1:     row.Sec_Img1     || '',
    Sec_href2:    row.Sec_href2    || '',
    Sec_target2:  row.Sec_target2  || '',
    Sec_Img2:     row.Sec_Img2     || '',
  }),
  columns: [
    { label: '#',           render: r => r.Sec_IdSec },
    { label: 'Descripción', render: r => r.Sec_DesSec },
    { label: 'Orden',       render: r => r.Sec_OrdenSec },
    { label: 'Page',        render: r => r.Sec_PageSec },
  ],
  renderForm: (form, set) => (
    <>
      <div className="col-12 col-md-6">
        <label className="form-label form-label-sm">Descripción *</label>
        <input className="form-control form-control-sm" value={form.Sec_DesSec}
          onChange={e => set('Sec_DesSec', e.target.value)} />
      </div>
      <div className="col-6 col-md-3">
        <label className="form-label form-label-sm">Orden</label>
        <input className="form-control form-control-sm" value={form.Sec_OrdenSec}
          onChange={e => set('Sec_OrdenSec', e.target.value)} />
      </div>
      <div className="col-6 col-md-3">
        <label className="form-label form-label-sm">Page</label>
        <input className="form-control form-control-sm" value={form.Sec_PageSec}
          onChange={e => set('Sec_PageSec', e.target.value)} />
      </div>
      <div className="col-12 col-md-4">
        <label className="form-label form-label-sm">href 1</label>
        <input className="form-control form-control-sm" value={form.Sec_href1}
          onChange={e => set('Sec_href1', e.target.value)} />
      </div>
      <div className="col-6 col-md-2">
        <label className="form-label form-label-sm">Target 1</label>
        <input className="form-control form-control-sm" value={form.Sec_target1}
          onChange={e => set('Sec_target1', e.target.value)} />
      </div>
      <div className="col-6 col-md-6">
        <label className="form-label form-label-sm">Imagen 1</label>
        <input className="form-control form-control-sm" value={form.Sec_Img1}
          onChange={e => set('Sec_Img1', e.target.value)} />
      </div>
      <div className="col-12 col-md-4">
        <label className="form-label form-label-sm">href 2</label>
        <input className="form-control form-control-sm" value={form.Sec_href2}
          onChange={e => set('Sec_href2', e.target.value)} />
      </div>
      <div className="col-6 col-md-2">
        <label className="form-label form-label-sm">Target 2</label>
        <input className="form-control form-control-sm" value={form.Sec_target2}
          onChange={e => set('Sec_target2', e.target.value)} />
      </div>
      <div className="col-6 col-md-6">
        <label className="form-label form-label-sm">Imagen 2</label>
        <input className="form-control form-control-sm" value={form.Sec_Img2}
          onChange={e => set('Sec_Img2', e.target.value)} />
      </div>
    </>
  ),
};

// ─── Config: Áreas ───────────────────────────────────────────────────────────
const areasConfig = {
  title: 'Áreas',
  api: {
    list:   ()        => setupApi.areas(),
    create: (data)    => setupApi.createArea(data),
    update: (id, data)=> setupApi.updateArea(id, data),
    remove: (id)      => setupApi.deleteArea(id),
  },
  idField: 'GER_IDGER',
  empty: {
    GER_DESGER: '',
  },
  fromRow: (row) => ({
    GER_DESGER: (row.GER_DESGER || '').trim(),
  }),
  columns: [
    { label: '#',           render: r => r.GER_IDGER },
    { label: 'Descripción', render: r => (r.GER_DESGER || '').trim() },
  ],
  renderForm: (form, set) => (
    <>
      <div className="col-12 col-md-6">
        <label className="form-label form-label-sm">Descripción *</label>
        <input className="form-control form-control-sm" value={form.GER_DESGER}
          onChange={e => set('GER_DESGER', e.target.value)} maxLength={20} />
      </div>
    </>
  ),
};

// ─── Config: Privacidad ──────────────────────────────────────────────────────
const privacidadConfig = {
  title: 'Privacidad',
  api: {
    list:   ()        => setupApi.privacidad(),
    create: (data)    => setupApi.createPrivacidad(data),
    update: (id, data)=> setupApi.updatePrivacidad(id, data),
    remove: (id)      => setupApi.deletePrivacidad(id),
  },
  idField: 'PRI_IdPrivac',
  empty: {
    PRI_DesPrivac: '',
  },
  fromRow: (row) => ({
    PRI_DesPrivac: row.PRI_DesPrivac || '',
  }),
  columns: [
    { label: '#',           render: r => r.PRI_IdPrivac },
    { label: 'Descripción', render: r => r.PRI_DesPrivac },
  ],
  renderForm: (form, set) => (
    <>
      <div className="col-12 col-md-6">
        <label className="form-label form-label-sm">Descripción *</label>
        <input className="form-control form-control-sm" value={form.PRI_DesPrivac}
          onChange={e => set('PRI_DesPrivac', e.target.value)} />
      </div>
    </>
  ),
};

// ─── Grupos (WHL_GRP_GRUPO) ──────────────────────────────────────────────────
const GRP_TYPES = [
  { value: 'B', label: 'B — Both (autorizaciones + menciones)' },
  { value: 'A', label: 'A — Solo autorizaciones' },
  { value: 'M', label: 'M — Solo menciones / mail' },
];

const gruposConfig = {
  title: 'Grupos',
  api: {
    list:   ()        => setupApi.grupos(),
    create: (data)    => setupApi.createGrupo(data),
    update: (id, data)=> setupApi.updateGrupo(id, data),
    remove: (id)      => setupApi.deleteGrupo(id),
  },
  idField: 'Grp_IdGrp',
  empty: { desGrp: '', filIdFil: '', grpType: 'B' },
  fromRow: (row) => ({
    desGrp:   row.Grp_DesGrp || '',
    filIdFil: row.Fil_idFil  ?? '',
    grpType:  (row.Grp_Type  || 'B').trim(),
  }),
  columns: [
    { label: '#',           render: r => r.Grp_IdGrp },
    { label: 'Descripción', render: r => r.Grp_DesGrp },
    { label: 'Tipo',        render: r => r.Grp_Type },
    { label: 'Filtro def.', render: r => r.Fil_idFil ?? '—' },
  ],
  extraApis: {
    opts: () => setupApi.filgrupoOptions(),
  },
  renderForm: (form, set, extraData, _isCopyOrNew) => (
    <>
      <div className="col-12 col-md-6">
        <label className="form-label form-label-sm">Descripción *</label>
        <input className="form-control form-control-sm" value={form.desGrp}
          onChange={e => set('desGrp', e.target.value)} />
      </div>
      <div className="col-12 col-md-3">
        <label className="form-label form-label-sm">Tipo</label>
        <CustomSelect value={form.grpType}
          onChange={e => set('grpType', e.target.value)}>
          {GRP_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </CustomSelect>
      </div>
      <div className="col-12 col-md-3">
        <label className="form-label form-label-sm">Filtro default</label>
        <CustomSelect value={form.filIdFil}
          onChange={e => set('filIdFil', e.target.value ? parseInt(e.target.value) : '')}>
          <option value="">— ninguno —</option>
          {(extraData?.opts?.filtros || []).map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
        </CustomSelect>
      </div>
    </>
  ),
};

function GruposCrud() { return <GenericCrud config={gruposConfig} />; }

// ─── FilGrupo (WHL_FGR_FILGRUPO) ─────────────────────────────────────────────
function FilGrupoCrud() {
  const [rows, setRows]       = useState([]);
  const [opts, setOpts]       = useState({ filtros: [], grupos: [] });
  const [loading, setLoading] = useState(true);
  const [form, setForm]       = useState({ filId: '', grpId: '' });
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  const load = () => {
    setLoading(true);
    Promise.all([setupApi.filgrupo(), setupApi.filgrupoOptions()])
      .then(([r, o]) => { setRows(r.data); setOpts(o.data); })
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    if (!form.filId || !form.grpId) return;
    setSaving(true);
    try {
      await setupApi.createFilgrupo({ filId: parseInt(form.filId), grpId: parseInt(form.grpId) });
      load();
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (filId, grpId) => {
    try {
      await setupApi.deleteFilgrupo(filId, grpId);
      setRows(prev => prev.filter(r => !(r.Fil_idFil === filId && r.Grp_IdGrp === grpId)));
    } catch (e) { setError(e.response?.data?.error || e.message); }
  };

  return (
    <div className="setup-content">
      <div className="setup-content-header"><h5 className="mb-0">Filtros por grupo</h5></div>
      {error && <div className="alert alert-danger py-1 px-2 mb-2">{error}</div>}

      {/* Alta */}
      <div className="d-flex gap-2 align-items-center mb-3 flex-wrap">
        <CustomSelect style={{ minWidth: 180 }} value={form.filId} onChange={e => setForm(p => ({ ...p, filId: e.target.value }))}>
          <option value="">— Filtro —</option>
          {opts.filtros.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
        </CustomSelect>
        <CustomSelect style={{ minWidth: 180 }} value={form.grpId} onChange={e => setForm(p => ({ ...p, grpId: e.target.value }))}>
          <option value="">— Grupo —</option>
          {opts.grupos.map(g => <option key={g.id} value={g.id}>{g.label}</option>)}
        </CustomSelect>
        <button className="button color1" onClick={handleAdd} disabled={saving || !form.filId || !form.grpId}>
          <i className="bi bi-plus-lg me-1" />{saving ? '…' : 'Agregar'}
        </button>
      </div>

      {/* Lista */}
      {loading ? <p className="text-muted">Cargando…</p> : (
        <table className="setup-table">
          <thead>
            <tr><th>Filtro</th><th>Grupo</th><th></th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={3} className="text-muted text-center py-3">Sin registros</td></tr>}
            {rows.map((r, i) => (
              <tr key={i}>
                <td>{r.FIL_DesFil}</td>
                <td>{r.Grp_DesGrp}</td>
                <td>
                  <button className="btn btn-sm btn-link p-0 text-danger" title="Eliminar"
                    onClick={() => handleDelete(r.Fil_idFil, r.Grp_IdGrp)}>
                    <i className="bi bi-trash" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── BracketPreview ──────────────────────────────────────────────────────────
const SAMPLE = {
  DOC_TITULO:   'Ejemplo Documento',
  DOC_SUBTITULO:'Subtítulo de muestra',
  DOC_CODIGO:   'COD-001',
  DOC_FECALTA:  '15/01/2025',
  DOC_FECPUBL:  '20/01/2025',
  DOC_IDDOCUM:  '1',
  DOC_ESTADO:   'Activo',
  ICONO:        'bi-file-text',
};

function fixMojibake(str) {
  try { return decodeURIComponent(escape(str)); } catch (e) { return str; }
}

function renderPreview(str) {
  if (!str) return '';
  let html = fixMojibake(str)
    // legacy bracket notation fallback: [div class="..."] → <div class="...">
    .replace(/\[(\/?[\w][\w-]*)([^\]]*)\]/g, '<$1$2>')
    // neutralize clicks in preview
    .replace(/onclick=(?:"[^"]*"|'[^']*')/gi, 'onclick="return false"');
  Object.entries(SAMPLE).forEach(([k, v]) => {
    html = html.replace(new RegExp(`\\{${k}\\}`, 'g'), v);
  });
  return html;
}
function BracketPreview({ html, rows }) {
  const preview = renderPreview(html);
  return (
    <div style={{
      border: '1px solid var(--border-color, #dee2e6)',
      borderRadius: 4,
      minHeight: rows ? `${rows * 1.55}rem` : '8rem',
      padding: '0.5rem',
      overflowY: 'auto',
      background: 'var(--background-color, #f8f9fa)',
      fontSize: '0.82rem',
      '--accent-color': '#70b9b0',
    }}>
      {preview
        ? (
          /* .services wrapper needed so .service-item CSS rules apply */
          <div className="services">
            {/* force Bootstrap col-* to full width so cards aren't tiny in a small container */}
            <style>{`.bp-preview [class*="col-"]{width:100%!important;flex:0 0 100%!important;max-width:100%!important}`}</style>
            <div className="bp-preview row g-2" dangerouslySetInnerHTML={{ __html: preview }} />
          </div>
        )
        : <span className="text-muted" style={{ fontSize: '0.75rem' }}>Vista previa</span>
      }
    </div>
  );
}

// ─── Config: Templates de vistas (WHL_VTE_VISTEMPLATE) ───────────────────────
const vistemplatesConfig = {
  title: 'Templates de vistas',
  api: {
    list:   ()        => setupApi.vistemplates(),
    create: (data)    => setupApi.createVistemplate(data),
    update: (id, data)=> setupApi.updateVistemplate(id, data),
    remove: (id)      => setupApi.deleteVistemplate(id),
  },
  idField: 'Vis_IdVis',
  empty: { Vis_Nombre: '', Vis_HTML: '' },
  fromRow: (row) => ({
    Vis_Nombre: (row.Vis_Nombre || '').trim(),
    Vis_HTML:   row.Vis_HTML || '',
  }),
  columns: [
    { label: '#',      render: r => r.Vis_IdVis },
    { label: 'Nombre', render: r => r.Vis_Nombre },
    { label: 'HTML',   render: r => <span className="text-truncate d-inline-block font-monospace" style={{ maxWidth: 340, fontSize: '0.75rem' }}>{r.Vis_HTML}</span> },
  ],
  renderForm: (form, set) => (
    <>
      <div className="col-12 col-md-5">
        <label className="form-label form-label-sm">Nombre *</label>
        <input className="form-control form-control-sm" maxLength={20} value={form.Vis_Nombre}
          onChange={e => set('Vis_Nombre', e.target.value)} />
      </div>
      <div className="w-100" />
      <div className="col-12 col-md-9">
        <label className="form-label form-label-sm">
          HTML
        </label>
        <textarea className="form-control form-control-sm font-monospace" rows={8}
          value={form.Vis_HTML}
          onChange={e => set('Vis_HTML', e.target.value)}
          placeholder="[div class=&quot;...&quot;]…[/div]"
          style={{ fontSize: '0.75rem', resize: 'vertical' }} />
      </div>
      <div className="col-12 col-md-3">
        <label className="form-label form-label-sm text-muted">Preview</label>
        <BracketPreview html={form.Vis_HTML} rows={8} />
      </div>
    </>
  ),
};

// ─── VisPanel: asignación de vistas en el form de tipodoc ────────────────────
const VIS_TYPES = [
  { key: 'card',     label: 'Tarjeta',    icon: 'bi-grid-1x2-fill' },
  { key: 'grid',     label: 'Grilla',     icon: 'bi-table' },
  { key: 'calendar', label: 'Calendario', icon: 'bi-calendar3' },
];

function VisPanel({ tdocId, templates }) {
  const [activeTab,    setActiveTab]    = useState('card');
  const [htmlMap,      setHtmlMap]      = useState({ card: '', grid: '', calendar: '' });
  const [saving,       setSaving]       = useState(false);
  const [msg,          setMsg]          = useState('');
  const [hoveredTpl,   setHoveredTpl]   = useState(null);

  useEffect(() => {
    if (!tdocId) return;
    setupApi.vistas(tdocId)
      .then(r => {
        const m = { card: '', grid: '', calendar: '' };
        (r.data || []).forEach(v => { m[v.Vis_Type] = v.Vis_HTML || ''; });
        setHtmlMap(m);
      })
      .catch(() => {});
  }, [tdocId]);

  useEffect(() => {
    if (templates && templates.length > 0) setHoveredTpl(templates[0]);
  }, [templates]);

  const applyTemplate = (tpl) => setHtmlMap(m => ({ ...m, [activeTab]: tpl.Vis_HTML || '' }));

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      const payload = VIS_TYPES.map(vt => ({ Vis_Type: vt.key, Vis_HTML: htmlMap[vt.key] || null }));
      await setupApi.saveVistas(tdocId, payload);
      setMsg('Guardado');
      setTimeout(() => setMsg(''), 2000);
    } catch (e) {
      setMsg(e.response?.data?.error || e.message);
    } finally { setSaving(false); }
  };

  if (!tdocId) return null;

  const accent = 'var(--accent-color, #70b9b0)';
  const tabStyle = (active) => active
    ? { color: accent, borderBottomColor: accent, fontWeight: 600 }
    : { color: accent, opacity: 0.55 };

  return (
    <div className="col-12 mt-2" style={{ borderTop: '1px solid var(--border-color, #dee2e6)', paddingTop: '0.75rem' }}>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <span style={{ fontWeight: 600, color: accent, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '.05em' }}>
          <i className="bi bi-eye me-1" />Vistas
        </span>
        <div className="d-flex align-items-center gap-2">
          {msg && <span className="text-success" style={{ fontSize: '0.78rem' }}>{msg}</span>}
          <button className="button color1" style={{ padding: '2px 12px', fontSize: '0.78rem' }}
            onClick={save} disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>

      <ul className="nav nav-tabs mb-2" style={{ fontSize: '0.8rem' }}>
        {VIS_TYPES.map(vt => (
          <li key={vt.key} className="nav-item">
            <button className={`nav-link py-1 px-3${activeTab === vt.key ? ' active' : ''}`}
              style={tabStyle(activeTab === vt.key)}
              onClick={() => setActiveTab(vt.key)}>
              <i className={`bi ${vt.icon} me-1`} />{vt.label}
            </button>
          </li>
        ))}
      </ul>

      {/* ── fila superior: HTML 60% | Preview 40% ── */}
      <div className="row g-2 mb-2">
        <div className="col-12 col-md-7">
          <label className="form-label form-label-sm text-muted">HTML</label>
          <textarea className="form-control form-control-sm font-monospace" rows={7}
            value={htmlMap[activeTab]}
            onChange={e => setHtmlMap(m => ({ ...m, [activeTab]: e.target.value }))}
            placeholder="<div class=&quot;...&quot;>…</div>"
            style={{ fontSize: '0.74rem', resize: 'vertical' }} />
        </div>
        <div className="col-12 col-md-5">
          <label className="form-label form-label-sm text-muted">Preview</label>
          <BracketPreview html={htmlMap[activeTab]} rows={7} />
        </div>
      </div>

      {/* ── fila inferior: HTML del template seleccionado | Lista de templates ── */}
      <div className="row g-2">
        <div className="col-12 col-md-7">
          {hoveredTpl
            ? <>
                <label className="form-label form-label-sm text-muted">
                  HTML — <span style={{ color: accent }}>{(hoveredTpl.Vis_Nombre || '').trim()}</span>
                  <span className="text-muted ms-2" style={{ fontWeight: 400 }}>(click en el nombre para aplicar)</span>
                </label>
                <textarea className="form-control form-control-sm font-monospace" rows={6}
                  readOnly value={hoveredTpl.Vis_HTML || ''}
                  style={{ fontSize: '0.74rem', background: 'var(--bs-light, #f8f9fa)', resize: 'vertical' }} />
              </>
            : <div className="d-flex align-items-center justify-content-center h-100"
                style={{ minHeight: 110, border: '1px dashed var(--border-color,#dee2e6)', borderRadius: 4, color: 'var(--text-muted,#6c757d)', fontSize: '0.77rem' }}>
                Pasá el mouse sobre un template para ver su HTML
              </div>
          }
        </div>
        <div className="col-12 col-md-5" style={{ borderLeft: '1px solid var(--border-color, #dee2e6)', paddingLeft: '0.75rem' }}>
          <label className="form-label form-label-sm text-muted">Templates <span style={{ fontWeight: 400 }}>(click para aplicar)</span></label>
          <div style={{ maxHeight: 260, overflowY: 'auto' }}>
            {(templates || []).length === 0
              ? <div className="text-muted" style={{ fontSize: '0.77rem' }}>Sin templates</div>
              : (templates || []).map(t => (
                <div key={t.Vis_IdVis} className="mb-2">
                  <button
                    className="d-block w-100 text-start px-2 py-1 mb-1"
                    style={{ fontSize: '0.78rem', border: '1px solid var(--border-color,#dee2e6)', borderRadius: 4,
                      background: hoveredTpl?.Vis_IdVis === t.Vis_IdVis ? 'rgba(112,185,176,.12)' : 'transparent', cursor: 'pointer' }}
                    onClick={() => applyTemplate(t)}>
                    <i className="bi bi-layout-text-sidebar me-1 text-muted" />
                    <strong>{(t.Vis_Nombre || '').trim()}</strong>
                  </button>
                  <div style={{ cursor: 'pointer' }} onClick={() => setHoveredTpl(hoveredTpl?.Vis_IdVis === t.Vis_IdVis ? null : t)}>
                    <BracketPreview html={t.Vis_HTML} rows={3} />
                  </div>
                </div>
              ))
            }
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Thin wrappers ───────────────────────────────────────────────────────────
function FiltrosCrud()     { return <GenericCrud config={filtrosConfig} />; }
function TipodocsCrud()    { return <GenericCrud config={tipodocsConfig} />; }
function VistasCrudPanel() { return <GenericCrud config={vistemplatesConfig} />; }
function CamposCrud()      { return <GenericCrud config={camposConfig} />; }
function PosibCrud()       { return <GenericCrud config={posibConfig} />; }
function SeccionesCrud()   { return <GenericCrud config={seccionesConfig} />; }
function AreasCrud()       { return <GenericCrud config={areasConfig} />; }
function PrivacidadCrud()  { return <GenericCrud config={privacidadConfig} />; }

// ─── UsrPer (Usuarios por perfil) ────────────────────────────────────────────
function UsrPerCrud() {
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [adding, setAdding]     = useState(false);
  const [form, setForm]         = useState({ Usr_IdUsr: '', Per_IdPer: '' });
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [usuarios, setUsuarios] = useState([]);
  const [perfiles, setPerfiles] = useState([]);

  const load = () => {
    setLoading(true);
    setupApi.usrper()
      .then(r => setRows(r.data))
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    setupApi.usrperUsuarios().then(r => setUsuarios(r.data)).catch(() => {});
    setupApi.perfiles().then(r => setPerfiles(r.data)).catch(() => {});
  }, []);

  const startAdd = () => { setForm({ Usr_IdUsr: '', Per_IdPer: '' }); setAdding(true); setError(''); };
  const cancel   = () => { setAdding(false); setError(''); };

  const save = async () => {
    if (!form.Usr_IdUsr || !form.Per_IdPer) { setError('Seleccioná usuario y perfil'); return; }
    setSaving(true); setError('');
    try {
      await setupApi.createUsrper(form);
      setAdding(false);
      load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const del = async (row) => {
    if (!window.confirm(`¿Eliminar "${row.Usr_ApeyNom} — ${row.Per_DesPer}"?`)) return;
    try {
      await setupApi.deleteUsrper(row.Usr_IdUsr, row.Per_IdPer);
      load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  return (
    <div className="setup-content">
      <div className="setup-content-header">
        <h5 className="mb-0">Usuarios por perfil</h5>
        {!adding && (
          <button className="button color1" onClick={startAdd}>
            <i className="bi bi-plus-lg me-1" />Nuevo
          </button>
        )}
      </div>

      {error && <div className="alert alert-danger py-1 px-2 mb-2">{error}</div>}

      {adding && (
        <div className="setup-form mb-3">
          <div className="setup-form-title mb-2">Nueva asignación</div>
          <div className="row g-2">
            <div className="col-12 col-md-5">
              <label className="form-label form-label-sm">Usuario *</label>
              <CustomSelect value={form.Usr_IdUsr}
                onChange={e => setForm(f => ({ ...f, Usr_IdUsr: e.target.value }))}>
                <option value="">— Seleccionar —</option>
                {usuarios.map(u => (
                  <option key={u.Usr_IdUsr} value={u.Usr_IdUsr}>{u.Usr_ApeyNom} ({u.Usr_login})</option>
                ))}
              </CustomSelect>
            </div>
            <div className="col-12 col-md-5">
              <label className="form-label form-label-sm">Perfil *</label>
              <CustomSelect value={form.Per_IdPer}
                onChange={e => setForm(f => ({ ...f, Per_IdPer: e.target.value }))}>
                <option value="">— Seleccionar —</option>
                {perfiles.map(p => (
                  <option key={p.Per_IdPer} value={p.Per_IdPer}>{p.Per_DesPer}</option>
                ))}
              </CustomSelect>
            </div>
          </div>
          <div className="mt-2 d-flex gap-2">
            <button className="button color1" onClick={save} disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
            <button className="button" onClick={cancel} disabled={saving}>Cancelar</button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-muted">Cargando…</p>
      ) : (
        <table className="setup-table">
          <thead>
            <tr>
              <th>Usuario</th>
              <th>Perfil</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={3} className="text-muted text-center py-3">Sin registros</td></tr>
            )}
            {rows.map(row => (
              <tr key={`${row.Usr_IdUsr}_${row.Per_IdPer}`}>
                <td>{row.Usr_ApeyNom} <span className="text-muted">({row.Usr_login})</span></td>
                <td>{row.Per_DesPer}</td>
                <td className="text-end">
                  <button className="btn btn-link btn-sm p-0 text-danger" title="Eliminar"
                    onClick={() => del(row)}>
                    <i className="bi bi-trash" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── PerfFun (Funciones por perfil) ─────────────────────────────────────────
function PerfFunCrud() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding]   = useState(false);
  const [form, setForm]       = useState({ Per_IdPer: '', Fun_IdFun: '' });
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [perfiles, setPerfiles]   = useState([]);
  const [funciones, setFunciones] = useState([]);

  const load = () => {
    setLoading(true);
    setupApi.perffun()
      .then(r => setRows(r.data))
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    setupApi.perfiles().then(r => setPerfiles(r.data)).catch(() => {});
    setupApi.funciones().then(r => setFunciones(r.data)).catch(() => {});
  }, []);

  const startAdd = () => { setForm({ Per_IdPer: '', Fun_IdFun: '' }); setAdding(true); setError(''); };
  const cancel   = () => { setAdding(false); setError(''); };

  const save = async () => {
    if (!form.Per_IdPer || !form.Fun_IdFun) { setError('Seleccioná perfil y función'); return; }
    setSaving(true); setError('');
    try {
      await setupApi.createPerffun(form);
      setAdding(false);
      load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const del = async (row) => {
    if (!window.confirm(`¿Eliminar "${row.Per_DesPer} — ${row.Fun_DesFun}"?`)) return;
    try {
      await setupApi.deletePerffun(row.Per_IdPer, row.Fun_IdFun);
      load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  return (
    <div className="setup-content">
      <div className="setup-content-header">
        <h5 className="mb-0">Funciones por perfil</h5>
        {!adding && (
          <button className="button color1" onClick={startAdd}>
            <i className="bi bi-plus-lg me-1" />Nuevo
          </button>
        )}
      </div>

      {error && <div className="alert alert-danger py-1 px-2 mb-2">{error}</div>}

      {adding && (
        <div className="setup-form mb-3">
          <div className="setup-form-title mb-2">Nueva asignación</div>
          <div className="row g-2">
            <div className="col-12 col-md-5">
              <label className="form-label form-label-sm">Perfil *</label>
              <CustomSelect value={form.Per_IdPer}
                onChange={e => setForm(f => ({ ...f, Per_IdPer: e.target.value }))}>
                <option value="">— Seleccionar —</option>
                {perfiles.map(p => (
                  <option key={p.Per_IdPer} value={p.Per_IdPer}>{p.Per_DesPer}</option>
                ))}
              </CustomSelect>
            </div>
            <div className="col-12 col-md-5">
              <label className="form-label form-label-sm">Función *</label>
              <CustomSelect value={form.Fun_IdFun}
                onChange={e => setForm(f => ({ ...f, Fun_IdFun: e.target.value }))}>
                <option value="">— Seleccionar —</option>
                {funciones
                  .filter(f => !form.Per_IdPer || !rows.some(
                    r => r.Per_IdPer === parseInt(form.Per_IdPer) && r.Fun_IdFun === f.Fun_IdFun
                  ))
                  .map(f => (
                    <option key={f.Fun_IdFun} value={f.Fun_IdFun}>{f.Fun_IdFun} — {f.Fun_DesFun}</option>
                  ))}
              </CustomSelect>
            </div>
          </div>
          <div className="mt-2 d-flex gap-2">
            <button className="button color1" onClick={save} disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
            <button className="button" onClick={cancel} disabled={saving}>Cancelar</button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-muted">Cargando…</p>
      ) : (
        <table className="setup-table">
          <thead>
            <tr>
              <th>Perfil</th>
              <th>Función</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={3} className="text-muted text-center py-3">Sin registros</td></tr>
            )}
            {rows.map(row => (
              <tr key={`${row.Per_IdPer}_${row.Fun_IdFun}`}>
                <td>{row.Per_DesPer}</td>
                <td>{row.Fun_IdFun} — {row.Fun_DesFun}</td>
                <td className="text-end">
                  <button className="btn btn-link btn-sm p-0 text-danger" title="Eliminar"
                    onClick={() => del(row)}>
                    <i className="bi bi-trash" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Config: Perfiles ────────────────────────────────────────────────────────
const perfilesConfig = {
  title: 'Perfiles',
  api: {
    list:   ()        => setupApi.perfiles(),
    create: (data)    => setupApi.createPerfil(data),
    update: (id, data)=> setupApi.updatePerfil(id, data),
    remove: (id)      => setupApi.deletePerfil(id),
  },
  idField: 'Per_IdPer',
  empty: { Per_DesPer: '' },
  fromRow: (row) => ({ Per_DesPer: row.Per_DesPer || '' }),
  columns: [
    { label: '#',          render: r => r.Per_IdPer },
    { label: 'Descripción', render: r => r.Per_DesPer },
  ],
  renderForm: (form, set) => (
    <div className="col-12 col-md-6">
      <label className="form-label form-label-sm">Descripción *</label>
      <input className="form-control form-control-sm" value={form.Per_DesPer}
        onChange={e => set('Per_DesPer', e.target.value)} />
    </div>
  ),
};

// ─── Config: Funciones ───────────────────────────────────────────────────────
const funcionesConfig = {
  title: 'Funciones',
  api: {
    list:   ()        => setupApi.funciones(),
    create: (data)    => setupApi.createFuncion(data),
    update: (id, data)=> setupApi.updateFuncion(id, data),
    remove: (id)      => setupApi.deleteFuncion(id),
  },
  idField: 'Fun_IdFun',
  empty: { Fun_IdFun: '', Fun_DesFun: '' },
  fromRow: (row) => ({ Fun_IdFun: row.Fun_IdFun, Fun_DesFun: row.Fun_DesFun || '' }),
  columns: [
    { label: '#',          render: r => r.Fun_IdFun },
    { label: 'Descripción', render: r => r.Fun_DesFun },
  ],
  renderForm: (form, set, _extra, isCopyOrNew) => (
    <>
      {isCopyOrNew && (
        <div className="col-4 col-md-2">
          <label className="form-label form-label-sm">ID</label>
          <input type="number" className="form-control form-control-sm" value={form.Fun_IdFun}
            onChange={e => set('Fun_IdFun', e.target.value)}
            placeholder="Auto" />
        </div>
      )}
      <div className="col-12 col-md-6">
        <label className="form-label form-label-sm">Descripción *</label>
        <input className="form-control form-control-sm" value={form.Fun_DesFun}
          onChange={e => set('Fun_DesFun', e.target.value)} />
      </div>
    </>
  ),
};

function PerfilesCrud()   { return <GenericCrud config={perfilesConfig} />; }
function FuncionesCrud()  { return <GenericCrud config={funcionesConfig} />; }

// ─── AdminPanel ──────────────────────────────────────────────────────────────
// ─── SqlConsole ──────────────────────────────────────────────────────────────
function SqlConsole() {
  const [query,   setQuery]   = useState('');
  const [result,  setResult]  = useState(null);  // { columns, rows } | { rowsAffected }
  const [error,   setError]   = useState('');
  const [loading, setLoading] = useState(false);

  const downloadCsv = () => {
    if (!result?.columns) return;
    const escape = v => {
      const s = v === null ? '' : String(v);
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [
      result.columns.join(','),
      ...result.rows.map(row => result.columns.map(col => escape(row[col])).join(',')),
    ];
    const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = 'resultado.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const run = async () => {
    if (!query.trim()) return;
    setLoading(true); setError(''); setResult(null);
    try {
      const res = await setupApi.sqlExec(query);
      setResult(res.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = e => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); run(); }
  };

  return (
    <div className="setup-content">
      <h5 className="mb-3" style={{ fontWeight: 700 }}>
        <i className="bi bi-terminal me-2" />Consola SQL
      </h5>

      <div className="mb-2">
        <textarea
          className="form-control form-control-sm font-monospace"
          rows={6}
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKey}
          placeholder="SELECT * FROM WHL_TDO_TIPODOC (NOLOCK) WHERE Emp_IdEmp = 1"
          style={{ fontSize: '0.82rem', resize: 'vertical' }}
          spellCheck={false}
        />
        <div className="d-flex align-items-center gap-2 mt-1">
          <button className="button color1" style={{ padding: '3px 18px', fontSize: '0.82rem' }}
            onClick={run} disabled={loading}>
            <i className="bi bi-play-fill me-1" />{loading ? 'Ejecutando…' : 'Ejecutar'}
          </button>
          <span className="text-muted" style={{ fontSize: '0.75rem' }}>Ctrl+Enter</span>
          {result?.rows && (
            <div className="d-flex align-items-center gap-2 ms-auto">
              <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                {result.rows.length} fila{result.rows.length !== 1 ? 's' : ''}
              </span>
              <button className="btn btn-outline-secondary btn-sm py-0 px-2" style={{ fontSize: '0.75rem' }}
                onClick={downloadCsv} title="Descargar CSV">
                <i className="bi bi-download me-1" />CSV
              </button>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="alert alert-danger py-2 px-3 font-monospace" style={{ fontSize: '0.78rem' }}>
          <i className="bi bi-exclamation-triangle me-2" />{error}
        </div>
      )}

      {result && !result.columns && (
        <div className="alert alert-success py-2 px-3" style={{ fontSize: '0.82rem' }}>
          <i className="bi bi-check-circle me-2" />
          {Array.isArray(result.rowsAffected)
            ? `${result.rowsAffected.reduce((a, b) => a + b, 0)} fila(s) afectada(s)`
            : 'Operación completada'}
        </div>
      )}

      {result?.columns && (
        <div style={{ overflowX: 'auto', maxHeight: 420, overflowY: 'auto', fontSize: '0.78rem' }}>
          <table className="table table-sm table-bordered table-hover mb-0" style={{ whiteSpace: 'nowrap' }}>
            <thead className="table-light" style={{ position: 'sticky', top: 0 }}>
              <tr>
                {result.columns.map(col => (
                  <th key={col} style={{ fontWeight: 600, padding: '4px 8px' }}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {result.rows.map((row, i) => (
                <tr key={i}>
                  {result.columns.map(col => (
                    <td key={col} style={{ padding: '3px 8px', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {row[col] === null ? <span className="text-muted fst-italic">NULL</span> : String(row[col])}
                    </td>
                  ))}
                </tr>
              ))}
              {result.rows.length === 0 && (
                <tr><td colSpan={result.columns.length} className="text-muted text-center py-3">Sin resultados</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AdminPanel() {
  const { user } = useAuth();
  const canBackup  = (user?.functions || []).includes(36);
  const canLogFree = (user?.functions || []).includes(36);
  const [disk, setDisk]         = useState(null);
  const [msg, setMsg]           = useState('');
  const [busy, setBusy]         = useState('');
  const [maintenance, setMaint] = useState(null); // null=cargando
  const [maintMsg, setMaintMsg] = useState('');

  useEffect(() => {
    setupApi.adminInfo().then(r => setDisk(r.data.disk)).catch(() => {});
    authApi.status().then(r => {
      setMaint(r.data.maintenance);
      setMaintMsg(r.data.message || '');
    }).catch(() => setMaint(false));
  }, []);

  const run = async (action, label) => {
    setBusy(action); setMsg('');
    try {
      const base = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const urls = { backup: `${base}/admin/db-backup`, logfree: `${base}/admin/db-log-free`, sync: `${base}/admin/onedrive-sync` };
      const r = await fetch(urls[action], { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('whl_token')}` } });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || r.statusText);
      setMsg(`${label} ejecutado correctamente`);
      if (action !== 'sync') setupApi.adminInfo().then(r => setDisk(r.data.disk)).catch(() => {});
    } catch (e) {
      setMsg(`Error: ${e.message}`);
    } finally { setBusy(''); }
  };

  const toggleMaintenance = async () => {
    const newVal = !maintenance;
    setBusy('maint');
    try {
      await authApi.setMaintenance(newVal, maintMsg);
      setMaint(newVal);
      setMsg(`Modo mantenimiento ${newVal ? 'activado' : 'desactivado'}`);
    } catch (e) {
      setMsg(`Error: ${e.message}`);
    } finally { setBusy(''); }
  };

  return (
    <div className="setup-content">
      <div className="setup-content-header"><h5 className="mb-0">Administración</h5></div>

      {msg && <div className="alert alert-info py-1 px-2 mb-3">{msg}</div>}

      {/* Mantenimiento */}
      <div className="card mb-3" style={{ maxWidth: 360 }}>
        <div className="card-body py-3">
          <h6 className="card-title mb-2"><i className="bi bi-tools me-2" />Modo mantenimiento</h6>
          <div className="d-flex align-items-center gap-3 mb-2">
            <span className={`badge ${maintenance ? 'bg-danger' : 'bg-success'}`}>
              {maintenance === null ? 'Cargando…' : maintenance ? 'ACTIVO' : 'INACTIVO'}
            </span>
            <button
              className={`button ${maintenance ? '' : 'color1'}`}
              onClick={toggleMaintenance}
              disabled={busy === 'maint' || maintenance === null}
            >
              <i className={`bi bi-toggle-${maintenance ? 'on' : 'off'} me-1`} />
              {busy === 'maint' ? 'Procesando…' : maintenance ? 'Desactivar' : 'Activar'}
            </button>
          </div>
          <div>
            <label className="form-label small text-muted mb-1">Mensaje (opcional, ej: "18:00 hs")</label>
            <input
              className="form-control form-control-sm"
              value={maintMsg}
              onChange={e => setMaintMsg(e.target.value)}
              placeholder="Horario estimado de vuelta"
            />
          </div>
        </div>
      </div>

      {/* Disco */}
      <div className="card mb-3" style={{ maxWidth: 360 }}>
        <div className="card-body py-3">
          <h6 className="card-title mb-3"><i className="bi bi-hdd me-2" />Espacio en disco</h6>
          {disk ? (
            <table className="setup-table">
              <tbody>
                <tr><td className="text-muted">Total</td><td><strong>{disk.total}</strong></td></tr>
                <tr><td className="text-muted">Usado</td><td><strong>{disk.used}</strong></td></tr>
                <tr><td className="text-muted">Libre</td><td><strong>{disk.free}</strong></td></tr>
                <tr><td className="text-muted">Uso</td><td><strong>{disk.pct}</strong></td></tr>
              </tbody>
            </table>
          ) : <p className="text-muted mb-0">Cargando…</p>}
        </div>
      </div>

      <div className="d-flex gap-2 flex-wrap">
        {canBackup && (
          <button className="button color1" onClick={() => run('backup', 'Backup BD')} disabled={!!busy}>
            <i className="fa-solid fa-database me-1" />{busy === 'backup' ? 'Ejecutando…' : 'Backup BD'}
          </button>
        )}
        {canLogFree && (
          <button className="button color1" onClick={() => run('logfree', 'Limpiar log BD')} disabled={!!busy}>
            <i className="fa-solid fa-broom me-1" />{busy === 'logfree' ? 'Ejecutando…' : 'Limpiar log BD'}
          </button>
        )}
        {canBackup && (
          <button className="button color1" onClick={() => run('sync', 'Sync OneDrive')} disabled={!!busy}>
            <i className="fa-brands fa-microsoft me-1" />{busy === 'sync' ? 'Sincronizando…' : 'Sync OneDrive'}
          </button>
        )}
        {!canBackup && !canLogFree && <p className="text-muted">Sin acciones disponibles.</p>}
      </div>
    </div>
  );
}

// ─── LogAccesos ───────────────────────────────────────────────────────────────
function LogAccesos() {
  const { user } = useAuth();
  const { idiId } = useTexts();
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [usuarios, setUsuarios] = useState([]);
  const [usrId, setUsrId]       = useState('');
  const [error, setError]       = useState('');
  const [expanded, setExpanded] = useState({});   // key: rowIndex → { loading, rows }

  const load = (uid) => {
    setLoading(true);
    setExpanded({});
    setupApi.logAccesos(uid || null)
      .then(r => setRows(r.data))
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load('');
    setupApi.usrperUsuarios().then(r => setUsuarios(r.data)).catch(() => {});
  }, []);

  const handleFilter = (val) => { setUsrId(val); load(val); };

  const toggleSesion = (i, row) => {
    if (expanded[i]) {
      setExpanded(prev => { const n = { ...prev }; delete n[i]; return n; });
      return;
    }
    setExpanded(prev => ({ ...prev, [i]: { loading: true, rows: [] } }));
    setupApi.logAccesosSesion(row.Usr_IdUsr, row.Acc_Fecha)
      .then(r => setExpanded(prev => ({ ...prev, [i]: { loading: false, rows: r.data } })))
      .catch(() => setExpanded(prev => ({ ...prev, [i]: { loading: false, rows: [] } })));
  };

  const isFiltrado = !!usrId;

  return (
    <div className="setup-content">
      <div className="setup-content-header">
        <h5 className="mb-0">Log de accesos</h5>
        <CustomSelect style={{ minWidth: 220 }}
          value={usrId} onChange={e => handleFilter(e.target.value)}>
          <option value="">— Todos los usuarios —</option>
          {usuarios.map(u => <option key={u.Usr_IdUsr} value={u.Usr_IdUsr}>{u.Usr_ApeyNom} ({u.Usr_login})</option>)}
        </CustomSelect>
      </div>
      {error && <div className="alert alert-danger py-1 px-2 mb-2">{error}</div>}
      {loading ? <p className="text-muted">Cargando…</p> : (
        <table className="setup-table">
          <thead>
            <tr>
              {!isFiltrado && <th style={{ width: 28 }}></th>}
              <th>Fecha</th>
              <th>Operación</th>
              <th>Usuario</th>
              <th>Proxy</th>
              <th>Doc</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={isFiltrado ? 5 : 6} className="text-muted text-center py-3">Sin registros</td></tr>
            )}
            {rows.map((r, i) => (
              <>
                <tr key={i}>
                  {!isFiltrado && (
                    <td>
                      <button
                        onClick={() => toggleSesion(i, r)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px', color: '#70b9b0' }}
                        title="Ver operaciones de esta sesión"
                      >
                        <i className={`fa-solid fa-chevron-${expanded[i] ? 'down' : 'right'}`} style={{ fontSize: '0.75rem' }} />
                      </button>
                    </td>
                  )}
                  <td className="text-nowrap">{formatDate(r.Acc_Fecha, user?.timezone, idiId, { showTime: true })}</td>
                  <td>{r.Acc_Oper}</td>
                  <td>{r.Usr_ApeyNom || r.Usr_IdUsr}</td>
                  <td>{r.Usr_Proxy && r.Usr_Proxy !== r.Usr_IdUsr ? (r.Proxy_Nom || r.Usr_Proxy) : '—'}</td>
                  <td>{r.Doc_iddocum || '—'}</td>
                </tr>
                {expanded[i] && (
                  <tr key={`exp-${i}`}>
                    <td></td>
                    <td colSpan={5} style={{ padding: '0 0 8px 24px', background: '#f8f9fa' }}>
                      {expanded[i].loading ? (
                        <span className="text-muted" style={{ fontSize: '0.8rem' }}>Cargando…</span>
                      ) : expanded[i].rows.length === 0 ? (
                        <span className="text-muted" style={{ fontSize: '0.8rem' }}>Sin operaciones en las próximas 6 horas</span>
                      ) : (
                        <table style={{ fontSize: '0.8rem', width: 'auto', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                          <colgroup>
                            <col style={{ width: 330 }} />
                            <col style={{ width: 210 }} />
                            <col style={{ width: 200 }} />
                            <col style={{ width: 100 }} />
                          </colgroup>
                          <thead>
                            <tr style={{ color: '#888' }}>
                              <th style={{ fontWeight: 500, paddingRight: 16 }}>Hora</th>
                              <th style={{ fontWeight: 500, paddingRight: 16 }}>Operación</th>
                              <th style={{ fontWeight: 500, paddingRight: 16 }}>Doc</th>
                              <th style={{ fontWeight: 500 }}>Archivo</th>
                            </tr>
                          </thead>
                          <tbody>
                            {expanded[i].rows.map((d, j) => (
                              <tr key={j} style={{ borderTop: '1px solid #eee' }}>
                                <td style={{ paddingRight: 16, paddingTop: 2, whiteSpace: 'nowrap' }}>
                                  {formatDate(d.Acc_Fecha, user?.timezone, idiId, { showTime: true })}
                                </td>
                                <td style={{ paddingRight: 16, paddingTop: 2 }}>{d.Acc_Oper}</td>
                                <td style={{ paddingRight: 16, paddingTop: 2 }}>{d.Doc_iddocum || '—'}</td>
                                <td style={{ paddingTop: 2 }}>{d.Arc_IdArc || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── LogBlog ──────────────────────────────────────────────────────────────────
function LogBlog() {
  const { user } = useAuth();
  const { idiId } = useTexts();
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [usuarios, setUsuarios] = useState([]);
  const [usrId, setUsrId]     = useState('');
  const [error, setError]     = useState('');

  const load = (uid) => {
    setLoading(true);
    setupApi.logBlog(uid || null)
      .then(r => setRows(r.data))
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load('');
    setupApi.usrperUsuarios().then(r => setUsuarios(r.data)).catch(() => {});
  }, []);

  const handleFilter = (val) => { setUsrId(val); load(val); };

  return (
    <div className="setup-content">
      <div className="setup-content-header">
        <h5 className="mb-0">Log de blog</h5>
        <CustomSelect style={{ minWidth: 220 }}
          value={usrId} onChange={e => handleFilter(e.target.value)}>
          <option value="">— Todos los usuarios —</option>
          {usuarios.map(u => <option key={u.Usr_IdUsr} value={u.Usr_IdUsr}>{u.Usr_ApeyNom} ({u.Usr_login})</option>)}
        </CustomSelect>
      </div>
      {error && <div className="alert alert-danger py-1 px-2 mb-2">{error}</div>}
      {loading ? <p className="text-muted">Cargando…</p> : (
        <table className="setup-table">
          <thead>
            <tr><th>Fecha</th><th>Usuario</th><th>Doc</th><th>Comentario</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={4} className="text-muted text-center py-3">Sin registros</td></tr>}
            {rows.map((r, i) => (
              <tr key={i}>
                <td className="text-nowrap">{formatDate(r.Acc_Fecha, user?.timezone, idiId, { showTime: true })}</td>
                <td>{r.Usr_ApeyNom || r.Usr_IdUsr}</td>
                <td>{r.Doc_IdDocum}</td>
                <td style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={r.Doc_Text}>{r.Doc_Text}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Config: Empresas ────────────────────────────────────────────────────────
const empresasConfig = {
  title: 'Empresas',
  api: {
    list:   ()        => setupApi.empresasCrud(),
    create: (data)    => setupApi.createEmpresa(data),
    update: (id,data) => setupApi.updateEmpresa(id, data),
    remove: (id)      => setupApi.deleteEmpresa(id),
  },
  idField: 'Emp_IdEmp',
  empty: { Emp_Descrip: '', Emp_Logo: '' },
  fromRow: (row) => ({ Emp_Descrip: row.Emp_Descrip || '', Emp_Logo: row.Emp_Logo || '' }),
  columns: [
    { label: '#',          render: r => r.Emp_IdEmp },
    { label: 'Descripción', render: r => r.Emp_Descrip },
    { label: 'Logo',       render: r => r.Emp_Logo },
  ],
  renderForm: (form, set) => (
    <>
      <div className="col-12 col-md-6">
        <label className="form-label form-label-sm">Descripción *</label>
        <input className="form-control form-control-sm" value={form.Emp_Descrip}
          onChange={e => set('Emp_Descrip', e.target.value)} />
      </div>
      <div className="col-12 col-md-6">
        <label className="form-label form-label-sm">Logo (ruta/URL)</label>
        <input className="form-control form-control-sm" value={form.Emp_Logo}
          onChange={e => set('Emp_Logo', e.target.value)} />
      </div>
    </>
  ),
};

// ─── Config: Parámetros ───────────────────────────────────────────────────────
const parametrosConfig = {
  title: 'Parámetros',
  api: {
    list:   ()        => setupApi.parametros(),
    create: (data)    => setupApi.createParametro(data),
    update: (id,data) => setupApi.updateParametro(id, data),
    remove: (id)      => setupApi.deleteParametro(id),
  },
  idField: 'Par_Nompar',
  empty: { Par_Nompar: '', Par_Despar: '', Par_Valpar: '' },
  fromRow: (row) => ({ Par_Nompar: row.Par_Nompar || '', Par_Despar: row.Par_Despar || '', Par_Valpar: row.Par_Valpar || '' }),
  columns: [
    { label: 'Nombre',      render: r => r.Par_Nompar },
    { label: 'Descripción', render: r => r.Par_Despar },
    { label: 'Valor',       render: r => r.Par_Valpar },
  ],
  renderForm: (form, set, _extra, isCopyOrNew) => (
    <>
      {isCopyOrNew && (
        <div className="col-6 col-md-3">
          <label className="form-label form-label-sm">Nombre (clave) *</label>
          <input className="form-control form-control-sm" value={form.Par_Nompar}
            onChange={e => set('Par_Nompar', e.target.value)} maxLength={10} />
        </div>
      )}
      <div className="col-12 col-md-4">
        <label className="form-label form-label-sm">Descripción</label>
        <input className="form-control form-control-sm" value={form.Par_Despar}
          onChange={e => set('Par_Despar', e.target.value)} maxLength={40} />
      </div>
      <div className="col-12 col-md-5">
        <label className="form-label form-label-sm">Valor</label>
        <input className="form-control form-control-sm" value={form.Par_Valpar}
          onChange={e => set('Par_Valpar', e.target.value)} />
      </div>
    </>
  ),
};

function EmpresasCrud()   { return <GenericCrud config={empresasConfig} />; }
function ParametrosCrud() { return <GenericCrud config={parametrosConfig} />; }

// ─── NotifAdminCrud ───────────────────────────────────────────────────────────
function NotifAdminCrud() {
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [editing, setEditing]   = useState(null); // null | 'new' | row
  const [form, setForm]         = useState({ Usr_IdUsr: '', Tdo_IdTDoc: '', Not_Tipo: 'U', Not_UsrGrp: '', Not_Data: 'N', Not_File: 'N' });
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [usuarios, setUsuarios] = useState([]);
  const [tipodocs, setTipodocs] = useState([]);
  const [destUsuarios, setDestUsuarios] = useState([]);
  const [grupos, setGrupos]     = useState([]);

  const load = () => {
    setLoading(true);
    setupApi.notifAdmin()
      .then(r => setRows(r.data))
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    setupApi.usrperUsuarios().then(r => { setUsuarios(r.data); setDestUsuarios(r.data); }).catch(() => {});
    setupApi.tipodocs().then(r => setTipodocs(r.data)).catch(() => {});
    setupApi.autorizGrupos().then(r => setGrupos(r.data)).catch(() => {});
  }, []);

  const startNew  = () => { setForm({ Usr_IdUsr: '', Tdo_IdTDoc: '', Not_Tipo: 'U', Not_UsrGrp: '', Not_Data: 'N', Not_File: 'N' }); setEditing('new'); setError(''); };
  const startEdit = row => { setForm({ Usr_IdUsr: row.Usr_IdUsr, Tdo_IdTDoc: row.Tdo_IdTDoc, Not_Tipo: row.Not_Tipo, Not_UsrGrp: row.Not_UsrGrp, Not_Data: (row.Not_Data||'N').trim(), Not_File: (row.Not_File||'N').trim() }); setEditing(row); setError(''); };
  const cancel    = () => { setEditing(null); setError(''); };

  const save = async () => {
    if (!form.Usr_IdUsr || !form.Tdo_IdTDoc || !form.Not_UsrGrp) { setError('Completá todos los campos'); return; }
    setSaving(true); setError('');
    try {
      await setupApi.saveNotifAdmin(form.Usr_IdUsr, form.Tdo_IdTDoc, form);
      setEditing(null); load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setSaving(false); }
  };

  const del = async row => {
    if (!window.confirm(`¿Eliminar notificación de "${row.Usr_ApeyNom}"?`)) return;
    try {
      await setupApi.deleteNotifAdmin(row.Usr_IdUsr, row.Tdo_IdTDoc, { Not_Tipo: row.Not_Tipo, Not_UsrGrp: row.Not_UsrGrp });
      load();
    } catch (e) { setError(e.response?.data?.error || e.message); }
  };

  const destOpts = form.Not_Tipo === 'U' ? destUsuarios : grupos;

  return (
    <div className="setup-content">
      <div className="setup-content-header">
        <h5 className="mb-0">Notificaciones por usuario</h5>
        {editing === null && <button className="button color1" onClick={startNew}><i className="bi bi-plus-lg me-1" />Nuevo</button>}
      </div>
      {error && <div className="alert alert-danger py-1 px-2 mb-2">{error}</div>}
      {editing !== null && (
        <div className="setup-form mb-3">
          <div className="setup-form-title mb-2">{editing === 'new' ? 'Nueva regla' : 'Editar regla'}</div>
          <div className="row g-2">
            <div className="col-12 col-md-4">
              <label className="form-label form-label-sm">Usuario *</label>
              {editing === 'new' ? (
                <CustomSelect value={form.Usr_IdUsr}
                  onChange={e => setForm(f => ({ ...f, Usr_IdUsr: e.target.value }))}>
                  <option value="">— Seleccionar —</option>
                  {usuarios.map(u => <option key={u.Usr_IdUsr} value={u.Usr_IdUsr}>{u.Usr_ApeyNom} ({u.Usr_login})</option>)}
                </CustomSelect>
              ) : (
                <input className="form-control form-control-sm" value={`${editing.Usr_ApeyNom} (${editing.Usr_login})`} disabled />
              )}
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label form-label-sm">Tipo doc *</label>
              {editing === 'new' ? (
                <CustomSelect value={form.Tdo_IdTDoc}
                  onChange={e => setForm(f => ({ ...f, Tdo_IdTDoc: e.target.value }))}>
                  <option value="">— Seleccionar —</option>
                  {tipodocs.map(t => <option key={t.Tdo_IdTDoc} value={t.Tdo_IdTDoc}>{t.Tdo_NombTDoc}</option>)}
                </CustomSelect>
              ) : (
                <input className="form-control form-control-sm" value={editing.Tdo_NombTDoc} disabled />
              )}
            </div>
            <div className="col-6 col-md-2">
              <label className="form-label form-label-sm">Dest. tipo</label>
              <CustomSelect value={form.Not_Tipo}
                onChange={e => setForm(f => ({ ...f, Not_Tipo: e.target.value, Not_UsrGrp: '' }))}>
                <option value="U">Usuario</option>
                <option value="G">Grupo</option>
              </CustomSelect>
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label form-label-sm">Destinatario *</label>
              <CustomSelect value={form.Not_UsrGrp}
                onChange={e => setForm(f => ({ ...f, Not_UsrGrp: e.target.value }))}>
                <option value="">— Seleccionar —</option>
                {destOpts.map(o => {
                  const id  = form.Not_Tipo === 'U' ? o.Usr_IdUsr  : o.Grp_IdGrp;
                  const lbl = form.Not_Tipo === 'U' ? `${o.Usr_ApeyNom} (${o.Usr_login})` : o.Grp_DesGrp;
                  return <option key={id} value={id}>{lbl}</option>;
                })}
              </CustomSelect>
            </div>
            <div className="col-6 col-md-2">
              <label className="form-label form-label-sm">Datos</label>
              <CustomSelect value={form.Not_Data}
                onChange={e => setForm(f => ({ ...f, Not_Data: e.target.value }))}>
                <option value="N">No</option>
                <option value="Y">Sí</option>
              </CustomSelect>
            </div>
            <div className="col-6 col-md-2">
              <label className="form-label form-label-sm">Archivos</label>
              <CustomSelect value={form.Not_File}
                onChange={e => setForm(f => ({ ...f, Not_File: e.target.value }))}>
                <option value="N">No</option>
                <option value="Y">Sí</option>
              </CustomSelect>
            </div>
          </div>
          <div className="mt-2 d-flex gap-2">
            <button className="button color1" onClick={save} disabled={saving}>{saving ? 'Guardando…' : 'Guardar'}</button>
            <button className="button" onClick={cancel} disabled={saving}>Cancelar</button>
          </div>
        </div>
      )}
      {loading ? <p className="text-muted">Cargando…</p> : (
        <table className="setup-table">
          <thead><tr><th>Usuario</th><th>Tipo doc</th><th>Destinatario</th><th>Datos</th><th>Archivos</th><th></th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="text-muted text-center py-3">Sin registros</td></tr>}
            {rows.map((row, i) => (
              <tr key={i}>
                <td>{row.Usr_ApeyNom}</td>
                <td>{row.Tdo_NombTDoc}</td>
                <td><span className="text-muted me-1">{row.Not_Tipo === 'U' ? 'Usr' : 'Grp'}</span>{row.Not_Destino}</td>
                <td className="text-center">{(row.Not_Data||'').trim() === 'Y' ? '✓' : '–'}</td>
                <td className="text-center">{(row.Not_File||'').trim() === 'Y' ? '✓' : '–'}</td>
                <td className="text-end text-nowrap">
                  <button className="btn btn-link btn-sm p-0 me-2" onClick={() => startEdit(row)}><i className="bi bi-pencil" /></button>
                  <button className="btn btn-link btn-sm p-0 text-danger" onClick={() => del(row)}><i className="bi bi-trash" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Config: Tesauro ─────────────────────────────────────────────────────────
const tesauroConfig = {
  title: 'Tesauro',
  api: {
    list:   ()        => setupApi.tesauro(),
    create: (data)    => setupApi.createTesauro(data),
    update: (id,data) => setupApi.updateTesauro(id, data),
    remove: (id)      => setupApi.deleteTesauro(id),
  },
  idField: 'Tes_IdTes',
  empty: { Tes_DesTes: '' },
  fromRow: (row) => ({ Tes_DesTes: row.Tes_DesTes || '' }),
  columns: [
    { label: '#',          render: r => r.Tes_IdTes },
    { label: 'Descripción', render: r => r.Tes_DesTes },
  ],
  renderForm: (form, set) => (
    <div className="col-12 col-md-6">
      <label className="form-label form-label-sm">Descripción *</label>
      <input className="form-control form-control-sm" value={form.Tes_DesTes}
        onChange={e => set('Tes_DesTes', e.target.value)} />
    </div>
  ),
};

// ─── Config: Descriptores ────────────────────────────────────────────────────
const descriptoresConfig = {
  title: 'Descriptores',
  api: {
    list:   ()        => setupApi.descriptores(),
    create: (data)    => setupApi.createDescriptor(data),
    update: (id,data) => setupApi.updateDescriptor(id, data),
    remove: (id)      => setupApi.deleteDescriptor(id),
  },
  extraApis: { tesauro: () => setupApi.tesauro() },
  idField: 'Des_IdDes',
  empty: { Tes_IdTes: '', DES_Nombre: '', DES_Expl: '', DES_Vale: 'N' },
  fromRow: (row) => ({
    Tes_IdTes:  row.Tes_IdTes  ?? '',
    DES_Nombre: row.DES_Nombre || '',
    DES_Expl:   row.DES_Expl   || '',
    DES_Vale:   (row.DES_Vale  || 'N').trim(),
  }),
  columns: [
    { label: '#',        render: r => r.Des_IdDes },
    { label: 'Tesauro',  render: r => r.Tes_DesTes || '—' },
    { label: 'Nombre',   render: r => r.DES_Nombre },
    { label: 'Vale',     render: r => (r.DES_Vale||'').trim() === 'Y' ? 'Sí' : 'No' },
  ],
  renderForm: (form, set, extra) => (
    <>
      <div className="col-12 col-md-4">
        <label className="form-label form-label-sm">Tesauro</label>
        <CustomSelect value={form.Tes_IdTes}
          onChange={e => set('Tes_IdTes', e.target.value)}>
          <option value="">— Sin tesauro —</option>
          {(extra.tesauro || []).map(t => <option key={t.Tes_IdTes} value={t.Tes_IdTes}>{t.Tes_DesTes}</option>)}
        </CustomSelect>
      </div>
      <div className="col-12 col-md-4">
        <label className="form-label form-label-sm">Nombre *</label>
        <input className="form-control form-control-sm" value={form.DES_Nombre}
          onChange={e => set('DES_Nombre', e.target.value)} />
      </div>
      <div className="col-12 col-md-4">
        <label className="form-label form-label-sm">Explicación</label>
        <input className="form-control form-control-sm" value={form.DES_Expl}
          onChange={e => set('DES_Expl', e.target.value)} />
      </div>
      <div className="col-6 col-md-2">
        <label className="form-label form-label-sm">Vale</label>
        <CustomSelect value={form.DES_Vale}
          onChange={e => set('DES_Vale', e.target.value)}>
          <option value="N">No</option>
          <option value="Y">Sí</option>
        </CustomSelect>
      </div>
    </>
  ),
};

// ─── DocteCrud (Descriptores por tipo doc) ────────────────────────────────────
function DocteCrud() {
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [adding, setAdding]       = useState(false);
  const [form, setForm]           = useState({ Tdo_IdTDoc: '', Des_IdDes: '' });
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const [tipodocs, setTipodocs]   = useState([]);
  const [descriptores, setDescriptores] = useState([]);

  const load = () => {
    setLoading(true);
    setupApi.doctes()
      .then(r => setRows(r.data))
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    setupApi.tipodocs().then(r => setTipodocs(r.data)).catch(() => {});
    setupApi.descriptores().then(r => setDescriptores(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    if (!form.Tdo_IdTDoc || !form.Des_IdDes) { setError('Seleccioná tipo doc y descriptor'); return; }
    setSaving(true); setError('');
    try {
      await setupApi.createDocte(form);
      setAdding(false); load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setSaving(false); }
  };

  const del = async row => {
    if (!window.confirm(`¿Eliminar "${row.Tdo_NombTDoc} — ${row.DES_Nombre}"?`)) return;
    try { await setupApi.deleteDocte(row.Tdo_IdTDoc, row.Des_IdDes); load(); }
    catch (e) { setError(e.response?.data?.error || e.message); }
  };

  const filteredDes = form.Tdo_IdTDoc
    ? descriptores.filter(d => !rows.some(r => r.Tdo_IdTDoc === parseInt(form.Tdo_IdTDoc) && r.Des_IdDes === d.Des_IdDes))
    : descriptores;

  return (
    <div className="setup-content">
      <div className="setup-content-header">
        <h5 className="mb-0">Descriptores por tipo de documento</h5>
        {!adding && <button className="button color1" onClick={() => { setForm({ Tdo_IdTDoc: '', Des_IdDes: '' }); setAdding(true); setError(''); }}><i className="bi bi-plus-lg me-1" />Nuevo</button>}
      </div>
      {error && <div className="alert alert-danger py-1 px-2 mb-2">{error}</div>}
      {adding && (
        <div className="setup-form mb-3">
          <div className="setup-form-title mb-2">Nueva asignación</div>
          <div className="row g-2">
            <div className="col-12 col-md-5">
              <label className="form-label form-label-sm">Tipo de documento *</label>
              <CustomSelect value={form.Tdo_IdTDoc}
                onChange={e => setForm(f => ({ ...f, Tdo_IdTDoc: e.target.value, Des_IdDes: '' }))}>
                <option value="">— Seleccionar —</option>
                {tipodocs.map(t => <option key={t.Tdo_IdTDoc} value={t.Tdo_IdTDoc}>{t.Tdo_NombTDoc}</option>)}
              </CustomSelect>
            </div>
            <div className="col-12 col-md-5">
              <label className="form-label form-label-sm">Descriptor *</label>
              <CustomSelect value={form.Des_IdDes}
                onChange={e => setForm(f => ({ ...f, Des_IdDes: e.target.value }))}>
                <option value="">— Seleccionar —</option>
                {filteredDes.map(d => <option key={d.Des_IdDes} value={d.Des_IdDes}>{d.DES_Nombre}</option>)}
              </CustomSelect>
            </div>
          </div>
          <div className="mt-2 d-flex gap-2">
            <button className="button color1" onClick={save} disabled={saving}>{saving ? 'Guardando…' : 'Guardar'}</button>
            <button className="button" onClick={() => { setAdding(false); setError(''); }} disabled={saving}>Cancelar</button>
          </div>
        </div>
      )}
      {loading ? <p className="text-muted">Cargando…</p> : (
        <table className="setup-table">
          <thead><tr><th>Tipo de documento</th><th>Descriptor</th><th></th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={3} className="text-muted text-center py-3">Sin registros</td></tr>}
            {rows.map((row, i) => (
              <tr key={i}>
                <td>{row.Tdo_NombTDoc}</td>
                <td>{row.DES_Nombre}</td>
                <td className="text-end">
                  <button className="btn btn-link btn-sm p-0 text-danger" onClick={() => del(row)}><i className="bi bi-trash" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function TesauroCrud()      { return <GenericCrud config={tesauroConfig} />; }
function DescriptoresCrud() { return <GenericCrud config={descriptoresConfig} />; }

// ─── AutorizCrud (compartido por autoriz y autrelac) ─────────────────────────
const AUT_FLAGS = [
  { key: 'Aut_Mod',     label: 'Modificar',        col: 'Mod'  },
  { key: 'Aut_Nue',     label: 'Nuevo',             col: 'Nue'  },
  { key: 'Aut_Eli',     label: 'Eliminar',          col: 'Eli'  },
  { key: 'Aut_ArcVis',  label: 'Ver archivos',      col: 'ArcV' },
  { key: 'Aut_ArcMod',  label: 'Mod. archivos',     col: 'ArcM' },
  { key: 'Aut_ArcBlq',  label: 'Bloq. archivos',    col: 'ArcB' },
  { key: 'Aut_Rel',     label: 'Relacionados',      col: 'Rel'  },
  { key: 'Aut_Com',     label: 'Compartir',         col: 'Com'  },
  { key: 'Aut_Dis',     label: 'Diseño',            col: 'Dis'  },
  { key: 'Aut_BlgVis',  label: 'Ver blog',          col: 'BlgV' },
  { key: 'Aut_BlgMod',  label: 'Mod. blog',         col: 'BlgM' },
  { key: 'Aut_DescVis', label: 'Ver descriptores',  col: 'DscV' },
  { key: 'Aut_DescMod', label: 'Mod. descriptores', col: 'DscM' },
];

const EMPTY_FLAGS = Object.fromEntries(AUT_FLAGS.map(f => [f.key, 0]));
const rowToFlags  = row  => Object.fromEntries(AUT_FLAGS.map(f => [f.key, row[f.key] ?? 0]));

function AutorizCrud({ tableKey, title }) {
  const apiMap = {
    autoriz:  { list: setupApi.autoriz,  grupos: setupApi.autorizGrupos,  create: setupApi.createAutoriz,  update: setupApi.updateAutoriz,  remove: setupApi.deleteAutoriz  },
    autrelac: { list: setupApi.autrelac, grupos: setupApi.autrelacGrupos, create: setupApi.createAutrelac, update: setupApi.updateAutrelac, remove: setupApi.deleteAutrelac },
  };
  const api = apiMap[tableKey];

  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | row
  const [form, setForm]       = useState({ Grp_IdGrp: '', Tdo_IdTDoc: '', Ger_IdGer: '', Pri_IdPrivac: '', ...EMPTY_FLAGS });
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [grupos, setGrupos]       = useState([]);
  const [tipodocs, setTipodocs]   = useState([]);
  const [areas, setAreas]         = useState([]);
  const [privacidad, setPrivacidad] = useState([]);

  const load = () => {
    setLoading(true);
    api.list()
      .then(r => setRows(r.data))
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    api.grupos().then(r => setGrupos(r.data)).catch(() => {});
    setupApi.tipodocs().then(r => setTipodocs(r.data)).catch(() => {});
    setupApi.areas().then(r => setAreas(r.data)).catch(() => {});
    setupApi.privacidad().then(r => setPrivacidad(r.data)).catch(() => {});
  }, []);

  const rowKey = r => `${r.Grp_IdGrp}_${r.Tdo_IdTDoc}_${r.Ger_IdGer}_${r.Pri_IdPrivac}`;

  const startNew  = () => { setForm({ Grp_IdGrp: '', Tdo_IdTDoc: '', Ger_IdGer: '', Pri_IdPrivac: '', ...EMPTY_FLAGS }); setEditing('new'); setError(''); };
  const startEdit = row => { setForm({ Grp_IdGrp: row.Grp_IdGrp, Tdo_IdTDoc: row.Tdo_IdTDoc, Ger_IdGer: row.Ger_IdGer, Pri_IdPrivac: row.Pri_IdPrivac, ...rowToFlags(row) }); setEditing(row); setError(''); };
  const cancel    = () => { setEditing(null); setError(''); };
  const setFlag   = (key, val) => setForm(f => ({ ...f, [key]: val ? 1 : 0 }));

  const save = async () => {
    if (!form.Grp_IdGrp || !form.Tdo_IdTDoc || !form.Ger_IdGer || !form.Pri_IdPrivac) {
      setError('Completá todos los campos clave'); return;
    }
    setSaving(true); setError('');
    try {
      if (editing === 'new') {
        await api.create(form);
      } else {
        await api.update(editing.Grp_IdGrp, editing.Tdo_IdTDoc, editing.Ger_IdGer, editing.Pri_IdPrivac, form);
      }
      setEditing(null);
      load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const del = async row => {
    if (!window.confirm(`¿Eliminar autorización de "${row.Grp_DesGrp}"?`)) return;
    try {
      await api.remove(row.Grp_IdGrp, row.Tdo_IdTDoc, row.Ger_IdGer, row.Pri_IdPrivac);
      load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const isNew = editing === 'new';

  return (
    <div className="setup-content">
      <div className="setup-content-header">
        <h5 className="mb-0">{title}</h5>
        {editing === null && (
          <button className="button color1" onClick={startNew}>
            <i className="bi bi-plus-lg me-1" />Nuevo
          </button>
        )}
      </div>

      {error && <div className="alert alert-danger py-1 px-2 mb-2">{error}</div>}

      {editing !== null && (
        <div className="setup-form mb-3">
          <div className="setup-form-title mb-2">{isNew ? 'Nueva autorización' : 'Editar permisos'}</div>
          <div className="row g-2 mb-2">
            <div className="col-12 col-md-3">
              <label className="form-label form-label-sm">Grupo *</label>
              {isNew ? (
                <CustomSelect value={form.Grp_IdGrp}
                  onChange={e => setForm(f => ({ ...f, Grp_IdGrp: e.target.value }))}>
                  <option value="">— Seleccionar —</option>
                  {grupos.map(g => <option key={g.Grp_IdGrp} value={g.Grp_IdGrp}>{g.Grp_DesGrp}</option>)}
                </CustomSelect>
              ) : (
                <input className="form-control form-control-sm" value={editing.Grp_DesGrp || ''} disabled />
              )}
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label form-label-sm">Tipo doc *</label>
              {isNew ? (
                <CustomSelect value={form.Tdo_IdTDoc}
                  onChange={e => setForm(f => ({ ...f, Tdo_IdTDoc: e.target.value }))}>
                  <option value="">— Seleccionar —</option>
                  {tipodocs.map(t => <option key={t.Tdo_IdTDoc} value={t.Tdo_IdTDoc}>{t.Tdo_NombTDoc}</option>)}
                </CustomSelect>
              ) : (
                <input className="form-control form-control-sm" value={editing.Tdo_NombTDoc || ''} disabled />
              )}
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label form-label-sm">Área *</label>
              {isNew ? (
                <CustomSelect value={form.Ger_IdGer}
                  onChange={e => setForm(f => ({ ...f, Ger_IdGer: e.target.value }))}>
                  <option value="">— Seleccionar —</option>
                  {areas.map(a => <option key={a.GER_IDGER} value={a.GER_IDGER}>{(a.GER_DESGER || '').trim()}</option>)}
                </CustomSelect>
              ) : (
                <input className="form-control form-control-sm" value={(editing.GER_DESGER || '').trim()} disabled />
              )}
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label form-label-sm">Privacidad *</label>
              {isNew ? (
                <CustomSelect value={form.Pri_IdPrivac}
                  onChange={e => setForm(f => ({ ...f, Pri_IdPrivac: e.target.value }))}>
                  <option value="">— Seleccionar —</option>
                  {privacidad.map(p => <option key={p.PRI_IdPrivac} value={p.PRI_IdPrivac}>{p.PRI_DesPrivac}</option>)}
                </CustomSelect>
              ) : (
                <input className="form-control form-control-sm" value={editing.PRI_DesPrivac || ''} disabled />
              )}
            </div>
          </div>
          <div className="row g-2">
            {AUT_FLAGS.map(f => (
              <div key={f.key} className="col-6 col-md-2">
                <div className="form-check">
                  <input className="form-check-input" type="checkbox" id={`aut_${f.key}`}
                    checked={!!form[f.key]}
                    onChange={e => setFlag(f.key, e.target.checked)} />
                  <label className="form-check-label form-label-sm" htmlFor={`aut_${f.key}`}>{f.label}</label>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-2 d-flex gap-2">
            <button className="button color1" onClick={save} disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
            <button className="button" onClick={cancel} disabled={saving}>Cancelar</button>
          </div>
        </div>
      )}

      {loading ? <p className="text-muted">Cargando…</p> : (
        <div style={{ overflowX: 'auto' }}>
          <table className="setup-table">
            <thead>
              <tr>
                <th>Grupo</th>
                <th>Tipo doc</th>
                <th>Área</th>
                <th>Privacidad</th>
                {AUT_FLAGS.map(f => <th key={f.key} title={f.label}>{f.col}</th>)}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={18} className="text-muted text-center py-3">Sin registros</td></tr>
              )}
              {rows.map(row => (
                <tr key={rowKey(row)} className={editing && rowKey(editing) === rowKey(row) ? 'table-active' : ''}>
                  <td>{row.Grp_DesGrp}</td>
                  <td>{row.Tdo_NombTDoc}</td>
                  <td>{(row.GER_DESGER || '').trim()}</td>
                  <td>{row.PRI_DesPrivac}</td>
                  {AUT_FLAGS.map(f => (
                    <td key={f.key} className="text-center">{row[f.key] ? '✓' : '–'}</td>
                  ))}
                  <td className="text-end text-nowrap">
                    <button className="btn btn-link btn-sm p-0 me-2" title="Editar" onClick={() => startEdit(row)}>
                      <i className="bi bi-pencil" />
                    </button>
                    <button className="btn btn-link btn-sm p-0 text-danger" title="Eliminar" onClick={() => del(row)}>
                      <i className="bi bi-trash" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Diseño de fichas ────────────────────────────────────────────────────────
function renderCardHtml(card) {
  let html = card.html || '';
  html = html.replace(/\[/g, '<').replace(/\]/g, '>');
  const iconClass = card.icon?.trim() || 'bi-file-earmark-text';
  html = html.replace(/\{ICONO\}/gi, iconClass);
  if (card.fields) {
    Object.entries(card.fields).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        html = html.replace(new RegExp(`\\{${key}\\}`, 'gi'), String(value));
      }
    });
  }
  html = html.replace(/\{[^}]+\}/g, '');
  html = html.trim();
  const outerMatch = html.match(/^<div([^>]*)>([\s\S]*)<\/div>$/s);
  const outerClass = outerMatch
    ? (outerMatch[1].match(/class="([^"]*)"/) || [])[1] || ''
    : '';
  const innerHtml = outerMatch ? outerMatch[2] : html;
  return { outerClass, innerHtml };
}

function DisenioPreview({ preview }) {
  if (!preview) return null;

  if (preview.tipDis === 'P') {
    if (!preview.hasRealDoc || !preview.card) return (
      <div className="text-muted small p-3">Sin documentos para mostrar.</div>
    );
    const { outerClass, innerHtml } = renderCardHtml(preview.card);
    return (
      <div className="row">
        <div className={outerClass || 'col-xl-3 col-lg-6 mb-3'}
          dangerouslySetInnerHTML={{ __html: innerHtml }} />
      </div>
    );
  }

  // tipDis === 'D'
  if (!preview.rows?.length) return (
    <div className="text-muted small p-3">Sin campos configurados.</div>
  );
  const fields = preview.rows.map(r => ({
    id:       r.Cmp_IdCampo,
    name:     r.Cmp_NombCampo,
    type:     r.Cmp_TipoCampo?.trim(),
    predef:   r.Cmp_Predef?.trim(),
    label:    r.Dis_Titulo?.trim() || r.Dis_Explic?.trim() || r.Cmp_NombCampo,
    tSize:    r.Dis_TSize,
    vSize:    r.Dis_VSize,
    newLine:  r.Dis_NewLine,
    readOnly: true,
    required: !!r.Dis_Oblig,
    options:  [],
    value:    r.value,
  }));
  return (
    <div>
      {!preview.hasRealDoc && (
        <div className="text-muted small mb-2 fst-italic">
          (sin documentos reales — valores de ejemplo)
        </div>
      )}
      <div className="row">
        {fields.map(field => (
          <FieldRenderer key={field.id} field={field} readOnly={true} />
        ))}
      </div>
    </div>
  );
}

function DisenioCrud() {
  const [tipodocs,   setTipodocs]   = useState([]);
  const [tdocId,     setTdocId]     = useState('');
  const [tipDis,     setTipDis]     = useState('P');
  const [rows,       setRows]       = useState([]);
  const [disponibles,setDisponibles]= useState([]);
  const [preview,    setPreview]    = useState(null);
  const [saving,     setSaving]     = useState({});
  const [addCampoId, setAddCampoId] = useState('');
  const [loadingPrev,setLoadingPrev]= useState(false);
  const [error,      setError]      = useState('');

  useEffect(() => {
    setupApi.tipodocs().then(r => {
      setTipodocs(r.data);
      if (r.data.length) setTdocId(r.data[0].Tdo_IdTDoc);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!tdocId) return;
    setError('');
    Promise.all([
      setupApi.disenio(tdocId, tipDis),
      setupApi.disenioDisponibles(tdocId, tipDis),
    ]).then(([r1, r2]) => {
      setRows(r1.data);
      setDisponibles(r2.data);
      setAddCampoId(r2.data[0]?.Cmp_IdCampo || '');
    }).catch(e => setError(e.response?.data?.error || e.message));
  }, [tdocId, tipDis]);

  useEffect(() => {
    if (!tdocId) return;
    setLoadingPrev(true);
    setupApi.disenioPreview(tdocId, tipDis)
      .then(r => setPreview(r.data))
      .catch(() => setPreview(null))
      .finally(() => setLoadingPrev(false));
  }, [tdocId, tipDis, rows]);

  const reload = () => {
    if (!tdocId) return;
    Promise.all([
      setupApi.disenio(tdocId, tipDis),
      setupApi.disenioDisponibles(tdocId, tipDis),
    ]).then(([r1, r2]) => {
      setRows(r1.data);
      setDisponibles(r2.data);
      setAddCampoId(r2.data[0]?.Cmp_IdCampo || '');
    }).catch(() => {});
  };

  const save = async (row) => {
    setSaving(s => ({ ...s, [row.Cmp_IdCampo]: true }));
    try {
      await setupApi.saveDisenio({
        tdocId, tipDis, campoId: row.Cmp_IdCampo,
        titulo: row.Dis_Titulo, explic: row.Dis_Explic,
        oblig: row.Dis_Oblig, readOnly: row.Dis_ReadOnly,
        defau: row.Dis_Defau, orden: row.Dis_Orden,
        newLine: row.Dis_NewLine,
        tSize: row.Dis_TSize, tFontFace: row.Dis_TFontFace,
        tFontSize: row.Dis_TFontSize, tFontColor: row.Dis_TFontColor,
        vSize: row.Dis_VSize, vFontFace: row.Dis_VFontFace,
        vFontSize: row.Dis_VFontSize, vFontColor: row.Dis_VFontColor,
        vFontBGColor: row.Dis_VFontBGColor,
        picW: row.Dis_PicW, picH: row.Dis_PicH,
      });
      reload();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(s => ({ ...s, [row.Cmp_IdCampo]: false }));
    }
  };

  const del = async (row) => {
    if (!window.confirm(`¿Quitar "${row.Cmp_NombCampo}" del diseño?`)) return;
    try {
      await setupApi.deleteDisenio(tdocId, tipDis, row.Cmp_IdCampo);
      reload();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const addCampo = async () => {
    if (!addCampoId) return;
    const maxOrden = rows.length ? Math.max(...rows.map(r => r.Dis_Orden)) + 1 : 1;
    const cmp = disponibles.find(c => c.Cmp_IdCampo === parseInt(addCampoId));
    await setupApi.saveDisenio({
      tdocId, tipDis, campoId: parseInt(addCampoId),
      titulo: '', explic: cmp?.Cmp_NombCampo || '',
      oblig: 0, readOnly: 0, defau: '', orden: maxOrden,
      newLine: 1, tSize: 60, tFontFace: 'Segoe UI', tFontSize: 4,
      tFontColor: '#444444', vSize: 40, vFontFace: 'Roboto',
      vFontSize: 4, vFontColor: '#333333', vFontBGColor: '',
      picW: '', picH: '',
    });
    reload();
  };

  const patch = (campoId, field, value) => {
    setRows(rs => rs.map(r => r.Cmp_IdCampo === campoId ? { ...r, [field]: value } : r));
  };

  return (
    <div className="setup-content">
      <h5 className="mb-3">Diseño de ficha</h5>

      {/* Filtros */}
      <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
        <div className="d-flex align-items-center gap-2">
          <label className="fw-semibold mb-0" style={{ whiteSpace: 'nowrap' }}>Tipo de documento</label>
          <CustomSelect value={tdocId} onChange={e => setTdocId(parseInt(e.target.value))}>
            {tipodocs.map(t => (
              <option key={t.Tdo_IdTDoc} value={t.Tdo_IdTDoc}>{t.Tdo_NombTDoc}</option>
            ))}
          </CustomSelect>
        </div>
        <div className="d-flex align-items-center gap-2">
          <label className="fw-semibold mb-0">Vista</label>
          <div className="btn-group btn-group-sm">
            <button className={`btn ${tipDis === 'P' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setTipDis('P')}>Tarjeta</button>
            <button className={`btn ${tipDis === 'D' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setTipDis('D')}>Detalle</button>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      <div className="d-flex flex-column gap-3">

        {/* Tabla de campos */}
        <div style={{ overflowX: 'auto' }}>
          <table className="table table-sm table-bordered align-middle" style={{ fontSize: 12 }}>
            <thead className="table-light">
              <tr>
                <th style={{ width: 50 }}>Orden</th>
                <th>Campo</th>
                <th>Título</th>
                <th>Explicación</th>
                <th style={{ width: 42 }} title="Obligatorio">Obl</th>
                <th style={{ width: 42 }} title="Solo lectura">RO</th>
                <th style={{ width: 42 }} title="Nueva línea">NL</th>
                <th style={{ width: 52 }}>Tam T</th>
                <th style={{ width: 52 }}>Tam V</th>
                <th style={{ width: 68 }}>Color T</th>
                <th style={{ width: 68 }}>Color V</th>
                <th style={{ width: 36 }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.Cmp_IdCampo}>
                  <td>
                    <input type="number" className="form-control form-control-sm" style={{ width: 50 }}
                      value={row.Dis_Orden} min={0}
                      onChange={e => patch(row.Cmp_IdCampo, 'Dis_Orden', parseInt(e.target.value) || 0)} />
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{row.Cmp_NombCampo}</td>
                  <td>
                    <input type="text" className="form-control form-control-sm"
                      value={row.Dis_Titulo || ''}
                      onChange={e => patch(row.Cmp_IdCampo, 'Dis_Titulo', e.target.value)} />
                  </td>
                  <td>
                    <input type="text" className="form-control form-control-sm"
                      value={row.Dis_Explic || ''}
                      onChange={e => patch(row.Cmp_IdCampo, 'Dis_Explic', e.target.value)} />
                  </td>
                  <td className="text-center">
                    <input type="checkbox" className="form-check-input"
                      checked={!!row.Dis_Oblig}
                      onChange={e => patch(row.Cmp_IdCampo, 'Dis_Oblig', e.target.checked ? 1 : 0)} />
                  </td>
                  <td className="text-center">
                    <input type="checkbox" className="form-check-input"
                      checked={!!row.Dis_ReadOnly}
                      onChange={e => patch(row.Cmp_IdCampo, 'Dis_ReadOnly', e.target.checked ? 1 : 0)} />
                  </td>
                  <td className="text-center">
                    <input type="checkbox" className="form-check-input"
                      checked={!!row.Dis_NewLine}
                      onChange={e => patch(row.Cmp_IdCampo, 'Dis_NewLine', e.target.checked ? 1 : 0)} />
                  </td>
                  <td>
                    <input type="number" className="form-control form-control-sm" style={{ width: 50 }}
                      value={row.Dis_TSize || 60}
                      onChange={e => patch(row.Cmp_IdCampo, 'Dis_TSize', parseInt(e.target.value) || 60)} />
                  </td>
                  <td>
                    <input type="number" className="form-control form-control-sm" style={{ width: 50 }}
                      value={row.Dis_VSize || 40}
                      onChange={e => patch(row.Cmp_IdCampo, 'Dis_VSize', parseInt(e.target.value) || 40)} />
                  </td>
                  <td>
                    <input type="color" className="form-control form-control-sm form-control-color"
                      style={{ width: 36, padding: 1 }}
                      value={row.Dis_TFontColor || '#444444'}
                      onChange={e => patch(row.Cmp_IdCampo, 'Dis_TFontColor', e.target.value)} />
                  </td>
                  <td>
                    <input type="color" className="form-control form-control-sm form-control-color"
                      style={{ width: 36, padding: 1 }}
                      value={row.Dis_VFontColor || '#333333'}
                      onChange={e => patch(row.Cmp_IdCampo, 'Dis_VFontColor', e.target.value)} />
                  </td>
                  <td>
                    <div className="d-flex gap-1">
                      <button className="btn btn-link btn-sm p-0 text-success" title="Guardar"
                        disabled={!!saving[row.Cmp_IdCampo]}
                        onClick={() => save(row)}>
                        {saving[row.Cmp_IdCampo]
                          ? <span className="spinner-border spinner-border-sm" />
                          : <i className="bi bi-check-lg" />}
                      </button>
                      <button className="btn btn-link btn-sm p-0 text-danger" title="Quitar"
                        onClick={() => del(row)}>
                        <i className="bi bi-trash" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Agregar campo */}
          {disponibles.length > 0 && (
            <div className="d-flex align-items-center gap-2 mt-2">
              <CustomSelect value={addCampoId} onChange={e => setAddCampoId(e.target.value)}>
                {disponibles.map(c => (
                  <option key={c.Cmp_IdCampo} value={c.Cmp_IdCampo}>{c.Cmp_NombCampo}</option>
                ))}
              </CustomSelect>
              <button className="btn btn-sm btn-outline-primary" onClick={addCampo}>
                <i className="bi bi-plus" /> Agregar campo
              </button>
            </div>
          )}
        </div>

        {/* Preview */}
        <div>
          <div className="card shadow-sm h-100">
            <div className="card-header py-2 d-flex align-items-center justify-content-between">
              <span className="fw-semibold" style={{ fontSize: 13 }}>
                {tipDis === 'P' ? 'Vista tarjeta' : 'Vista detalle'}
              </span>
              {loadingPrev && <span className="spinner-border spinner-border-sm text-secondary" />}
            </div>
            <div className="card-body p-3" style={{ overflowY: 'auto' }}>
              <DisenioPreview preview={preview} />
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

// ─── Temas (WHL_TEM_TEMA) ────────────────────────────────────────────────────
const temasConfig = {
  title:   'Temas',
  idField: 'Tem_IdTem',
  api: {
    list:   ()         => setupApi.temas(),
    create: (data)     => setupApi.createTema(data),
    update: (id, data) => setupApi.updateTema(id, data),
    remove: (id)       => setupApi.deleteTema(id),
  },
  empty: { Tem_desTem: '', Tem_HTMLPage: '', Tem_ASPPage: '', Tem_OrdenTem: '' },
  fromRow: (row) => ({
    Tem_desTem:   row.Tem_desTem   || '',
    Tem_HTMLPage: row.Tem_HTMLPage || '',
    Tem_ASPPage:  row.Tem_ASPPage  || '',
    Tem_OrdenTem: row.Tem_OrdenTem ?? '',
  }),
  columns: [
    { label: '#',           render: r => r.Tem_IdTem },
    { label: 'Descripción', render: r => r.Tem_desTem },
    { label: 'Orden',       render: r => r.Tem_OrdenTem },
  ],
  renderForm: (form, set) => (
    <>
      <div className="col-12 col-md-8">
        <label className="form-label form-label-sm">Descripción *</label>
        <input className="form-control form-control-sm" value={form.Tem_desTem}
          onChange={e => set('Tem_desTem', e.target.value)} />
      </div>
      <div className="col-6 col-md-4">
        <label className="form-label form-label-sm">Orden</label>
        <input type="number" className="form-control form-control-sm" value={form.Tem_OrdenTem}
          onChange={e => set('Tem_OrdenTem', e.target.value)} />
      </div>
      <div className="col-12 col-md-6">
        <label className="form-label form-label-sm">HTML Page</label>
        <input className="form-control form-control-sm" value={form.Tem_HTMLPage}
          onChange={e => set('Tem_HTMLPage', e.target.value)} />
      </div>
      <div className="col-12 col-md-6">
        <label className="form-label form-label-sm">ASP Page</label>
        <input className="form-control form-control-sm" value={form.Tem_ASPPage}
          onChange={e => set('Tem_ASPPage', e.target.value)} />
      </div>
    </>
  ),
};
function TemasCrud() { return <GenericCrud config={temasConfig} />; }

// ─── FilTema (WHL_FTE_FILTEM) ─────────────────────────────────────────────────
function FilTemaCrud() {
  const [rows, setRows]       = useState([]);
  const [opts, setOpts]       = useState({ filtros: [], temas: [] });
  const [loading, setLoading] = useState(true);
  const [form, setForm]       = useState({ filId: '', temId: '' });
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  const load = () => {
    setLoading(true);
    setupApi.filtema()
      .then(r => { setRows(r.data.rows); setOpts({ filtros: r.data.filtros, temas: r.data.temas }); })
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    if (!form.filId || !form.temId) return;
    setSaving(true);
    try {
      await setupApi.createFiltema({ filId: parseInt(form.filId), temId: parseInt(form.temId) });
      load();
      setForm({ filId: '', temId: '' });
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (temId, filId) => {
    try {
      await setupApi.deleteFiltema(temId, filId);
      setRows(prev => prev.filter(r => !(r.Tem_idTem === temId && r.Fil_idFil === filId)));
    } catch (e) { setError(e.response?.data?.error || e.message); }
  };

  return (
    <div className="setup-content">
      <div className="setup-content-header"><h5 className="mb-0">Filtros por tema</h5></div>
      {error && <div className="alert alert-danger py-1 px-2 mb-2">{error}</div>}

      <div className="d-flex gap-2 align-items-center mb-3 flex-wrap">
        <CustomSelect style={{ minWidth: 180 }} value={form.filId} onChange={e => setForm(p => ({ ...p, filId: e.target.value }))}>
          <option value="">— Filtro —</option>
          {opts.filtros.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
        </CustomSelect>
        <CustomSelect style={{ minWidth: 180 }} value={form.temId} onChange={e => setForm(p => ({ ...p, temId: e.target.value }))}>
          <option value="">— Tema —</option>
          {opts.temas.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
        </CustomSelect>
        <button className="button color1" onClick={handleAdd} disabled={saving || !form.filId || !form.temId}>
          <i className="bi bi-plus-lg me-1" />{saving ? '…' : 'Agregar'}
        </button>
      </div>

      {loading ? <p className="text-muted">Cargando…</p> : (
        <table className="setup-table">
          <thead>
            <tr><th>Filtro</th><th>Tema</th><th></th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={3} className="text-muted text-center py-3">Sin registros</td></tr>}
            {rows.map((r, i) => (
              <tr key={i}>
                <td>{r.FIL_DesFil}</td>
                <td>{r.Tem_desTem}</td>
                <td>
                  <button className="btn btn-sm btn-link p-0 text-danger" title="Eliminar"
                    onClick={() => handleDelete(r.Tem_idTem, r.Fil_idFil)}>
                    <i className="bi bi-trash" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Panel map ───────────────────────────────────────────────────────────────
const PANELS = {
  adminPanel:  <AdminPanel />,
  sqlConsole:  <SqlConsole />,
  empresas:    <EmpresasCrud />,
  parametros:  <ParametrosCrud />,
  notifAdmin:  <NotifAdminCrud />,
  logAccesos:  <LogAccesos />,
  logBlog:     <LogBlog />,
  filtros:     <FiltrosCrud />,
  filgrupo:    <FilGrupoCrud />,
  temas:       <TemasCrud />,
  filtema:     <FilTemaCrud />,
  tiposDoc:    <TipodocsCrud />,
  vistas:      <VistasCrudPanel />,
  campos:      <CamposCrud />,
  posib:       <PosibCrud />,
  secciones:   <SeccionesCrud />,
  areas:       <AreasCrud />,
  privacidad:  <PrivacidadCrud />,
  grupos:      <GruposCrud />,
  perfiles:    <PerfilesCrud />,
  funciones:   <FuncionesCrud />,
  perfFun:     <PerfFunCrud />,
  usrPer:      <UsrPerCrud />,
  autoriz:     <AutorizCrud tableKey="autoriz"  title="Autorizaciones directas" />,
  autrelac:    <AutorizCrud tableKey="autrelac" title="Autorizaciones relacionadas" />,
  tesauro:     <TesauroCrud />,
  descr:       <DescriptoresCrud />,
  descrTdo:    <DocteCrud />,
  disenio:     <DisenioCrud />,
};

// ─── Placeholder para secciones aún no implementadas ─────────────────────────
function ComingSoon({ label }) {
  return (
    <div className="setup-content d-flex align-items-center justify-content-center" style={{ minHeight: 200 }}>
      <p className="text-muted">{label} — próximamente</p>
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────
export default function Setup() {
  const [active, setActive] = useState('filtros');

  const panel = PANELS[active] ?? <ComingSoon label={active} />;

  return (
    <Layout sidebar={<Sidebar active={active} onSelect={setActive} />}>
      {panel}
    </Layout>
  );
}
